import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate,
  useSearchParams } from 'react-router-dom';
import { mensajesAPI,
  acuerdosAPI,
  resenasAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { trackMessageSent,
  trackConversationStarted } from '../services/analytics';
import { getPerfilDisplayName } from '../utils/miEntidadNombreDefecto';
import { resolveBandaFromUser,
  resolveCofradiaFromUser } from '../utils/acuerdoParties';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Input,
  IconButton,
  Card,
  Flex,
  Badge,
  Icon,
  Spinner,
  Button,
  useDisclosure,
  Textarea,
  Alert,
  Collapsible,
  Heading,
  Progress,
  Tabs,
  Image,
  Wrap,
  Checkbox,
  Separator,
  Field,
  List,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaSearch,
  FaPaperPlane,
  FaCircle,
  FaComments,
  FaWifi,
  FaHandshake,
  FaCheck,
  FaTimes,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaMusic,
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaExclamationTriangle,
  FaChurch,
  FaStar,
  FaSmile,
  FaCheckDouble,
  FaPaperclip,
  FaFileAlt,
  FaChevronDown,
} from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaMensajesSteps } from '../tours/bandaMensajesTour';
import ReviewForm from './ReviewForm.jsx';
import AcuerdoDetailsModal from './AcuerdoDetailsModal.jsx';
import { getEventoInicioTimestampMs, isEventoEfectivamenteFinalizado } from '../utils/eventoInicio.js';
import { resolveUploadsUrl } from '../utils/staticUrl.js';
import { parseAcuerdoAdjuntosInfo } from '../utils/acuerdoAdjuntosInfo.js';

const MAX_ACUERDO_ADJUNTOS_UI = 10;

const ACUERDO_CREATE_STEPS = [
  {
    title: 'Contexto',
    subtitle: 'Revisa con quién propones el acuerdo y el evento vinculado a la conversación',
  },
  {
    title: 'Términos del acuerdo',
    subtitle: 'Describe la propuesta de forma clara; es el paso obligatorio',
  },
  {
    title: 'Documentación y notas',
    subtitle:
      'Prioriza los archivos adjuntos: contratos, pliegos o fotos que respalden el acuerdo; las notas son opcionales',
  },
  {
    title: 'Resumen y envío',
    subtitle: 'Confirma los datos antes de enviar la solicitud a la otra parte',
  },
];

/** Hora inicio / fin en UI (compat. campo legacy `hora`). */
function horasEventoConversacion(ev) {
  if (!ev) return { inicio: null, fin: null };
  const inicioRaw = (ev.horaInicio && String(ev.horaInicio).trim()) || (ev.hora && String(ev.hora).trim()) || '';
  const finRaw = ev.horaFin && String(ev.horaFin).trim();
  return {
    inicio: inicioRaw || null,
    fin: finRaw || null,
  };
}

/** Listado mensajes: finalizado = COMPLETED en servidor o ya pasó fecha+hora de inicio */
function eventoConversacionFinalizada(ev) {
  return !!ev && isEventoEfectivamenteFinalizado(ev);
}

function eventoConversacionActiva(ev) {
  const s = (ev?.status || '').toLowerCase();
  if (s === 'cancelled') return false;
  return !eventoConversacionFinalizada(ev);
}

const Messages = ({ userType = 'banda' }) => {
  const navigate = useNavigate();
  useProductTour('banda-mensajes', bandaMensajesSteps, { autoStart: false, listenGlobal: userType === 'banda' });
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { 
    isConnected, 
    sendMessage: socketSendMessage, 
    sendTyping,
    markAsRead,
    joinConversation,
    subscribeToMessages,
    subscribeToTyping,
    isUserOnline 
  } = useSocket();
  
  
  // Leer parámetros de URL
  const conversacionIdFromUrl = searchParams.get('conversacion');
  const tabFromUrl = searchParams.get('tab'); // 0=Activos, 1=Finalizados, 2=Cancelados
  const eventoIdFromUrl = searchParams.get('evento'); // ID del evento a desplegar
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const selectedBg = useColorModeValue('purple.50', 'purple.900');
  const messageBg = useColorModeValue('gray.100', 'gray.700');
  const myMessageBg = useColorModeValue('purple.500', 'purple.600');

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [expandedEventoIds, setExpandedEventoIds] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [activeTab, setActiveTab] = useState(0); // 0: Activos, 1: Finalizados, 2: Cancelados
  
  // Estados para acuerdos
  const [activeAcuerdo, setActiveAcuerdo] = useState(null);
  const [isLoadingAcuerdo, setIsLoadingAcuerdo] = useState(false);
  const [hasOtherActiveAcuerdo, setHasOtherActiveAcuerdo] = useState(false);
  const [acuerdoDescription, setAcuerdoDescription] = useState('');
  const [acuerdoNotas, setAcuerdoNotas] = useState('');
  const [acuerdoAdjuntosFiles, setAcuerdoAdjuntosFiles] = useState([]);
  const [acuerdoCreateStep, setAcuerdoCreateStep] = useState(0);
  const [acuerdoAceptaDisclaimer, setAcuerdoAceptaDisclaimer] = useState(false);
  const [aceptaDisclaimerAceptarAcuerdo, setAceptaDisclaimerAceptarAcuerdo] = useState(false);
  const [respuestaNotas, setRespuestaNotas] = useState('');
  const [isSubmittingAcuerdo, setIsSubmittingAcuerdo] = useState(false);
  const acuerdoAdjuntosInputRef = useRef(null);
  const [acuerdoNewAdjuntosFiles, setAcuerdoNewAdjuntosFiles] = useState([]);
  const [acuerdoNewAdjuntosAceptaDisclaimer, setAcuerdoNewAdjuntosAceptaDisclaimer] = useState(false);
  const acuerdoNewAdjuntosInputRef = useRef(null);
  
  // Modal de acuerdo
  const {
    open: isAcuerdoModalOpen,
    onOpen: onAcuerdoModalOpenBase,
    onClose: onAcuerdoModalCloseBase,
  } = useDisclosure();

  const resetAcuerdoCreateForm = useCallback(() => {
    setAcuerdoDescription('');
    setAcuerdoNotas('');
    setAcuerdoAdjuntosFiles([]);
    setAcuerdoCreateStep(0);
    setAcuerdoAceptaDisclaimer(false);
  }, []);

  const onAcuerdoModalClose = useCallback(() => {
    resetAcuerdoCreateForm();
    onAcuerdoModalCloseBase();
  }, [resetAcuerdoCreateForm, onAcuerdoModalCloseBase]);

  const onAcuerdoModalOpen = useCallback(() => {
    resetAcuerdoCreateForm();
    onAcuerdoModalOpenBase();
  }, [resetAcuerdoCreateForm, onAcuerdoModalOpenBase]);
  const { open: isRespondModalOpen, onOpen: onRespondModalOpenBase, onClose: onRespondModalCloseBase } = useDisclosure();
  const onRespondModalOpen = useCallback(() => {
    setAceptaDisclaimerAceptarAcuerdo(false);
    setRespuestaNotas('');
    onRespondModalOpenBase();
  }, [onRespondModalOpenBase]);
  const onRespondModalClose = useCallback(() => {
    setAceptaDisclaimerAceptarAcuerdo(false);
    onRespondModalCloseBase();
  }, [onRespondModalCloseBase]);
  const { open: isRejectModalOpen, onOpen: onRejectModalOpen, onClose: onRejectModalClose } = useDisclosure();
  const { open: isCancelModalOpen, onOpen: onCancelModalOpen, onClose: onCancelModalClose } = useDisclosure();
  const { open: isRescindModalOpen, onOpen: onRescindModalOpen, onClose: onRescindModalClose } = useDisclosure();
  const { open: isAcuerdoDetailsOpen, onOpen: onAcuerdoDetailsOpen, onClose: onAcuerdoDetailsClose } = useDisclosure();
  const [rescindReason, setRescindReason] = useState('');

  const acuerdoDetailAdjuntos = useMemo(
    () => parseAcuerdoAdjuntosInfo(activeAcuerdo?.adjuntosInfo),
    [activeAcuerdo?.adjuntosInfo],
  );
  const adjuntoPreviewCardBg = useColorModeValue('white', 'gray.700');
  const acuerdoFileRowBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.15)');
  const acuerdoAdjuntosZoneBg = useColorModeValue('purple.50', 'rgba(88, 28, 135, 0.35)');
  const acuerdoAdjuntosZoneBorder = useColorModeValue('purple.400', 'purple.400');
  const acuerdoAdjuntosIconBg = useColorModeValue('purple.600', 'purple.300');
  const acuerdoAdjuntosHeadingColor = useColorModeValue('purple.800', 'purple.100');
  const acuerdoAdjuntosLeadTextColor = useColorModeValue('gray.700', 'gray.200');
  const acuerdoAdjuntosHintColor = useColorModeValue('gray.600', 'gray.300');
  const acuerdoAdjuntosListItemBg = useColorModeValue('white', 'gray.800');
  const acuerdoAdjuntosListItemBorder = useColorModeValue('purple.200', 'purple.500');
  const acuerdoAdjuntosEmptyDashedBg = useColorModeValue('white', 'gray.900');
  
  // Estados para reseñas
  const { open: isReviewModalOpen, onOpen: onReviewModalOpen, onClose: onReviewModalClose } = useDisclosure();
  const [eventoParaResenar, setEventoParaResenar] = useState(null);
  const [hasReviewForEvent, setHasReviewForEvent] = useState(false);
  
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isLoadingOlderRef = useRef(false);
  
  // Paginación de mensajes
  const [totalMessages, setTotalMessages] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const MESSAGE_PAGE_SIZE = 30;

  const toggleEventoExpanded = useCallback((eventoId) => {
    if (!eventoId) return;
    setExpandedEventoIds((prev) =>
      prev.includes(eventoId)
        ? prev.filter((id) => id !== eventoId)
        : [...prev, eventoId],
    );
  }, []);

  const ensureEventoExpanded = useCallback((eventoId) => {
    if (!eventoId) return;
    setExpandedEventoIds((prev) =>
      prev.includes(eventoId) ? prev : [...prev, eventoId],
    );
  }, []);

  // Cargar conversaciones
  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await mensajesAPI.getConversaciones();
      const convs = response.data.map(conv => {
        // Obtener el otro participante
        const otherParticipant = conv.participantes.find(p => p.userId !== user?.id);
        const otherUser = otherParticipant?.user;
        const profileData = otherUser?.banda || otherUser?.cofradia;
        
        return {
          id: conv.id,
          name: profileData?.nombre || 'Usuario',
          avatar: profileData?.avatar,
          lastMessage: conv.lastMessage || 'Sin mensajes',
          updatedAt: conv.updatedAt,
          otherUserId: otherUser?.id,
          otherUserType: otherUser?.userType,
          participantes: conv.participantes,
          unreadCount: conv.unreadCount || 0, // ✅ Agregar contador de no leídos
          // Información del evento asociado
          evento: conv.evento ? {
            id: conv.evento.id,
            nombre: conv.evento.nombre,
            fecha: conv.evento.fecha,
            horaInicio: conv.evento.horaInicio,
            horaFin: conv.evento.horaFin,
            hora: conv.evento.hora,
            ubicacion: conv.evento.ubicacion,
            status: conv.evento.status?.toLowerCase(),
            bandaId: conv.evento.bandaId,
            bandaName: conv.evento.banda?.nombre,
            bandaAvatar: conv.evento.banda?.avatar,
            cofradiaId: conv.evento.cofradiaId,
            cofradiaName: conv.evento.cofradia?.nombre,
            cofradiaAvatar: conv.evento.cofradia?.avatar,
          } : null,
        };
      });
      setConversations(convs);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
      toaster.create({
        title: 'Error',
        description: 'No se pudieron cargar las conversaciones',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Cargar mensajes de una conversación (los más recientes)
  const loadMessages = useCallback(async (conversacionId) => {
    try {
      setIsLoadingMessages(true);
      setHasMoreMessages(false);
      const response = await mensajesAPI.getMensajes(conversacionId, { limit: MESSAGE_PAGE_SIZE, offset: 0 });
      const { data: msgs, total } = response.data;
      setMessages(msgs || []);
      setTotalMessages(total || 0);
      setHasMoreMessages((msgs || []).length < total);
      isNearBottomRef.current = true;
      
      // Unirse a la conversación en WebSocket
      await joinConversation(conversacionId);
      
      // Marcar como leídos via WebSocket
      markAsRead(conversacionId);
      
      // Marcar como leídos via API (persiste en DB)
      try {
        await mensajesAPI.markAsRead(conversacionId);
        // Recargar conversaciones para actualizar el contador de no leídos
        loadConversations();
      } catch (error) {
        console.error('Error marcando mensajes como leídos:', error);
      }
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [joinConversation, markAsRead, loadConversations]);

  // Cargar mensajes más antiguos (scroll hacia arriba, estilo Telegram)
  const loadOlderMessages = useCallback(async () => {
    if (!selectedConversation || !hasMoreMessages || isLoadingOlderRef.current) return;
    
    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);
    
    try {
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;
      
      const offset = messages.length;
      const response = await mensajesAPI.getMensajes(selectedConversation.id, { limit: MESSAGE_PAGE_SIZE, offset });
      const { data: olderMsgs, total } = response.data;
      
      if (olderMsgs && olderMsgs.length > 0) {
        setMessages(prev => [...olderMsgs, ...prev]);
        setTotalMessages(total);
        setHasMoreMessages(offset + olderMsgs.length < total);
        
        // Mantener la posición de scroll después de insertar mensajes antiguos
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
          }
        });
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Error cargando mensajes anteriores:', error);
    } finally {
      setIsLoadingOlder(false);
      isLoadingOlderRef.current = false;
    }
  }, [selectedConversation, hasMoreMessages, messages.length]);

  // Verificar si ya existe una reseña para el evento actual
  const checkIfReviewExists = useCallback(async (eventoId) => {
    if (!eventoId) return;
    
    try {
      const response = await resenasAPI.hasReviewForEvent(eventoId);
      setHasReviewForEvent(response.data?.hasReview || false);
    } catch (error) {
      console.error('Error verificando reseña:', error);
      setHasReviewForEvent(false);
    }
  }, []);

  // Cargar conversaciones al montar
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Verificar si existe reseña cuando el evento ya puede valorarse (completado o pasó inicio)
  useEffect(() => {
    if (selectedConversation?.evento?.id && eventoConversacionFinalizada(selectedConversation.evento)) {
      checkIfReviewExists(selectedConversation.evento.id);
    } else {
      setHasReviewForEvent(false);
    }
  }, [selectedConversation, checkIfReviewExists]);

  // Seleccionar conversación automáticamente si viene de una notificación
  useEffect(() => {
    if (conversacionIdFromUrl && conversations.length > 0) {
      const conv = conversations.find(c => c.id === conversacionIdFromUrl);
      console.log('Buscando conversación con ID:', conversacionIdFromUrl);
      console.log('Conversación encontrada:', conv);
      console.log('Todas las conversaciones:', conversations.map(c => ({ id: c.id, name: c.name })));
      
      if (conv && selectedConversation?.id !== conv.id) {
        setSelectedConversation(conv);
        
        // Limpiar el parámetro de la URL después de un pequeño delay
        setTimeout(() => {
          setSearchParams({}, { replace: true });
        }, 500);
        
        // Scroll solo dentro del panel de lista (evitar scrollIntoView → ventana entera)
        setTimeout(() => {
          const element = document.getElementById(`conversation-${conv.id}`);
          if (!element) return;
          let scrollParent = element.parentElement;
          while (scrollParent) {
            const { overflowY } = window.getComputedStyle(scrollParent);
            const canScroll =
              (overflowY === 'auto' || overflowY === 'scroll') &&
              scrollParent.scrollHeight > scrollParent.clientHeight + 1;
            if (canScroll) {
              const rect = element.getBoundingClientRect();
              const parentRect = scrollParent.getBoundingClientRect();
              const delta =
                rect.top - parentRect.top - scrollParent.clientHeight / 2 + rect.height / 2;
              scrollParent.scrollTo({
                top: Math.max(0, scrollParent.scrollTop + delta),
                behavior: 'smooth',
              });
              return;
            }
            scrollParent = scrollParent.parentElement;
          }
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }, 200);
      } else if (!conv) {
        console.warn('No se encontró la conversación con ID:', conversacionIdFromUrl);
      }
    }
  }, [conversacionIdFromUrl, conversations, selectedConversation, setSearchParams]);

  // Manejar navegación desde eventos cancelados/finalizados (tab + eventoId)
  useEffect(() => {
    if (tabFromUrl && conversations.length > 0) {
      const tabIndex = parseInt(tabFromUrl, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
        setActiveTab(tabIndex);
        
        // Si también viene el eventoId, abrir el accordion correspondiente (solo para cofradías)
        if (eventoIdFromUrl && userType === 'cofradia') {
          ensureEventoExpanded(eventoIdFromUrl);
        }
        
        // Limpiar parámetros después de procesarlos
        setTimeout(() => {
          setSearchParams({}, { replace: true });
        }, 500);
      }
    }
  }, [tabFromUrl, eventoIdFromUrl, conversations, ensureEventoExpanded, setSearchParams, userType]);

  // Seleccionar conversación automáticamente si viene con contactId (desde eventos)
  useEffect(() => {
    const contactIdFromUrl = searchParams.get('contact');
    if (contactIdFromUrl && conversations.length > 0 && !selectedConversation) {
      const conv = conversations.find(c => c.otherUserId === contactIdFromUrl);
      if (conv) {
        setSelectedConversation(conv);
        // Limpiar el parámetro de la URL para evitar reseleccionar al recargar
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, conversations, selectedConversation, setSearchParams]);

  // Cargar mensajes cuando se selecciona una conversación
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation?.id, loadMessages]);

  // Suscribirse a nuevos mensajes
  useEffect(() => {
    if (!selectedConversation) return;

    const unsubscribe = subscribeToMessages(selectedConversation.id, (newMessage) => {
      setMessages(prev => [...prev, newMessage]);
      
      // Si el mensaje no es mío, marcar como leído
      if (newMessage.senderId !== user?.id) {
        markAsRead(selectedConversation.id);
      }
    });

    return () => unsubscribe();
  }, [selectedConversation?.id, subscribeToMessages, markAsRead, user?.id]);

  // Suscribirse a indicador de escritura
  useEffect(() => {
    if (!selectedConversation) return;

    const unsubscribe = subscribeToTyping(selectedConversation.id, ({ userId, isTyping }) => {
      setTypingUsers(prev => ({
        ...prev,
        [userId]: isTyping,
      }));
    });

    return () => unsubscribe();
  }, [selectedConversation?.id, subscribeToTyping]);

  // Suscribirse a nuevos mensajes globales (para actualizar lista)
  useEffect(() => {
    const unsubscribe = subscribeToMessages('global', (data) => {
      // Actualizar la lista de conversaciones
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === data.conversacionId) {
            return {
              ...conv,
              lastMessage: data.mensaje.content,
              updatedAt: data.mensaje.createdAt,
            };
          }
          return conv;
        });
        // Ordenar por fecha de actualización
        return updated.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      });
    });

    return () => unsubscribe();
  }, [subscribeToMessages]);

  /** Solo desplaza el panel de mensajes (overflow auto), no la página. */
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const run = () => {
      const container = messagesContainerRef.current;
      if (!container) return;
      const maxTop = container.scrollHeight - container.clientHeight;
      if (maxTop <= 0) return;
      if (behavior === 'instant' || behavior === 'auto') {
        container.scrollTop = maxTop;
      } else {
        container.scrollTo({ top: maxTop, behavior: 'smooth' });
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }, []);

  // Comprobar si el usuario está cerca del final del scroll
  const checkIfNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 150; // px desde el fondo
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Scroll automático cuando se selecciona una conversación (instant, al fondo)
  useEffect(() => {
    if (selectedConversation && messages.length > 0 && !isLoadingMessages) {
      scrollToBottom('instant');
      isNearBottomRef.current = true;
    }
  }, [selectedConversation?.id, messages.length, isLoadingMessages, scrollToBottom]);

  // Auto-scroll solo si el usuario está cerca del fondo (para nuevos mensajes)
  useEffect(() => {
    if (messages.length > 0 && isNearBottomRef.current && !isLoadingOlder) {
      scrollToBottom('smooth');
    }
  }, [messages.length, scrollToBottom, isLoadingOlder]);

  // Detectar scroll para cargar mensajes antiguos y rastrear posición
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      isNearBottomRef.current = checkIfNearBottom();

      // Cargar mensajes más antiguos al llegar arriba
      if (container.scrollTop < 80 && hasMoreMessages && !isLoadingOlderRef.current) {
        loadOlderMessages();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, loadOlderMessages, checkIfNearBottom]);

  // Manejar escritura
  const handleTyping = () => {
    if (selectedConversation) {
      sendTyping(selectedConversation.id, true);
      
      // Limpiar timeout anterior
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Dejar de escribir después de 2 segundos
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(selectedConversation.id, false);
      }, 2000);
    }
  };

  // Enviar mensaje
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation || isSending) return;

    const content = messageText.trim();
    const conversacionId = selectedConversation.id;
    setMessageText('');
    setIsSending(true);

    // Dejar de indicar que está escribiendo
    sendTyping(conversacionId, false);

    const appendMensaje = (mensaje) => {
      if (!mensaje) return;
      setMessages((prev) => {
        if (mensaje.id && prev.some((m) => m.id === mensaje.id)) return prev;
        return [...prev, mensaje];
      });
    };

    try {
      // La room se une al abrir la conversación (loadMessages). Aquí solo
      // reafirmamos sin await: un join lento no debe retrasar el envío.
      if (isConnected) {
        void joinConversation(conversacionId);
      }

      let mensaje = null;
      if (isConnected) {
        try {
          mensaje = await socketSendMessage(conversacionId, content);
        } catch (wsError) {
          console.warn('Envío WebSocket falló; usando REST', wsError);
          const response = await mensajesAPI.sendMensaje(conversacionId, content);
          mensaje = response.data;
        }
      } else {
        const response = await mensajesAPI.sendMensaje(conversacionId, content);
        mensaje = response.data;
      }

      appendMensaje(mensaje);

      // Actualizar última mensaje en la lista
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === conversacionId) {
            return { ...conv, lastMessage: content, updatedAt: new Date().toISOString() };
          }
          return conv;
        }),
      );
      trackMessageSent(user?.userType);
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      toaster.create({
        title: 'Error',
        description: error?.message || 'No se pudo enviar el mensaje',
        type: 'error',
        duration: 3000,
      });
      // Restaurar el mensaje
      setMessageText(content);
    } finally {
      setIsSending(false);
    }
  };

  // Filtrar conversaciones por búsqueda y por pestaña
  const filteredConversations = conversations.filter(conv => {
    // Filtro de búsqueda
    const matchesSearch = conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conv.evento?.nombre || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    // Filtro por pestaña
    const eventoStatus = conv.evento?.status?.toLowerCase();

    if (activeTab === 0) {
      return eventoConversacionActiva(conv.evento);
    } else if (activeTab === 1) {
      return eventoConversacionFinalizada(conv.evento);
    } else if (activeTab === 2) {
      // Cancelados
      return eventoStatus === 'cancelled';
    }
    
    return true;
  });

  // Agrupar conversaciones por evento
  const conversationsGroupedByEvent = React.useMemo(() => {
    const grouped = {};
    filteredConversations.forEach(conv => {
      const eventoId = conv.evento?.id || 'sin-evento';
      if (!grouped[eventoId]) {
        grouped[eventoId] = {
          evento: conv.evento,
          conversaciones: [],
        };
      }
      grouped[eventoId].conversaciones.push(conv);
    });
    
    // Ordenar por inicio del evento (fecha + hora), más recientes primero
    return Object.values(grouped).sort((a, b) => {
      const ta = a.evento ? getEventoInicioTimestampMs(a.evento) : 0;
      const tb = b.evento ? getEventoInicioTimestampMs(b.evento) : 0;
      return tb - ta;
    });
  }, [filteredConversations]);

  // Abrir el grupo del evento al seleccionar una conversación (solo cofradías)
  useEffect(() => {
    if (selectedConversation?.evento?.id && userType === 'cofradia') {
      ensureEventoExpanded(selectedConversation.evento.id);
    }
  }, [selectedConversation, userType, ensureEventoExpanded]);

  // Formatear timestamp
  const formatTimestamp = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)} días`;
    return d.toLocaleDateString('es-ES');
  };

  const renderCofradiaEventGroups = useCallback((theme) => {
    const palettes = {
      purple: {
        headerBg: 'purple.50',
        headerBgUnread: 'purple.100',
        headerHover: 'purple.200',
        icon: 'purple.600',
        title: 'purple.800',
        subtitle: 'purple.600',
        badge: 'purple',
      },
      green: {
        headerBg: 'green.50',
        headerBgUnread: 'green.100',
        headerHover: 'green.100',
        icon: 'green.600',
        title: 'green.800',
        subtitle: 'green.600',
        badge: 'green',
      },
      red: {
        headerBg: 'red.50',
        headerBgUnread: 'red.100',
        headerHover: 'red.100',
        icon: 'red.600',
        title: 'red.800',
        subtitle: 'red.600',
        badge: 'red',
      },
    };
    const p = palettes[theme] || palettes.purple;

    return conversationsGroupedByEvent.map((group) => {
      const eventoId = group.evento?.id || `sin-evento-${group.conversaciones[0]?.id}`;
      const isExpanded = expandedEventoIds.includes(eventoId);
      const eventoUnreadCount = group.conversaciones.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      );

      return (
        <Box key={eventoId} w="full" borderBottom="1px" borderColor={borderColor}>
          <Box
            as="button"
            type="button"
            w="full"
            px={4}
            py={3}
            bg={eventoUnreadCount > 0 ? p.headerBgUnread : p.headerBg}
            _hover={{ bg: p.headerHover }}
            onClick={() => toggleEventoExpanded(eventoId)}
            textAlign="left"
          >
            <HStack gap={3}>
              <Icon as={FaCalendarAlt} color={p.icon} boxSize={4} />
              <VStack align="start" gap={0} flex={1}>
                <Text
                  fontWeight={eventoUnreadCount > 0 ? 'extrabold' : 'bold'}
                  fontSize="sm"
                  color={p.title}
                  lineClamp={1}
                >
                  {group.evento?.nombre || 'Sin evento'}
                </Text>
                {group.evento?.fecha && (
                  <Text fontSize="xs" color={p.subtitle}>
                    {new Date(group.evento.fecha).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </Text>
                )}
              </VStack>
              <HStack gap={2}>
                {eventoUnreadCount > 0 && (
                  <Badge
                    colorPalette="red"
                    rounded="full"
                    fontSize="xs"
                    minW="24px"
                    h="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                  >
                    {eventoUnreadCount}
                  </Badge>
                )}
                <Badge colorPalette={p.badge} rounded="full" px={2}>
                  <HStack gap={1}>
                    <Icon as={FaMusic} boxSize={3} />
                    <Text>{group.conversaciones.length}</Text>
                  </HStack>
                </Badge>
                <Icon
                  as={FaChevronDown}
                  color={p.icon}
                  boxSize={3}
                  transform={isExpanded ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s"
                />
              </HStack>
            </HStack>
          </Box>
          <Collapsible.Root open={isExpanded} unmountOnExit={false}>
            <Collapsible.Content>
              <Box>
                {group.conversaciones.map((conv) => (
                  <Box
                    key={conv.id}
                    id={`conversation-${conv.id}`}
                    w="full"
                    p={4}
                    pl={6}
                    cursor="pointer"
                    bg={selectedConversation?.id === conv.id ? selectedBg : 'transparent'}
                    borderBottom="1px"
                    borderColor={borderColor}
                    _hover={{ bg: selectedConversation?.id === conv.id ? selectedBg : 'gray.50' }}
                    onClick={() => setSelectedConversation(conv)}
                    transition="background 0.2s"
                  >
                    <HStack gap={3} align="start">
                      <Box position="relative">
                        <Avatar.Root size="sm"><Avatar.Fallback name={conv.name} /><Avatar.Image src={conv.avatar} /></Avatar.Root>
                        {isUserOnline(conv.otherUserId) && (
                          <Box
                            position="absolute"
                            bottom={0}
                            right={0}
                            w={2.5}
                            h={2.5}
                            bg="green.500"
                            border="2px solid white"
                            rounded="full"
                          />
                        )}
                      </Box>
                      <VStack gap={0} align="start" flex={1} minW={0}>
                        <HStack w="full" justify="space-between">
                          <Text fontWeight="bold" fontSize="sm" lineClamp={1}>
                            {conv.name}
                          </Text>
                          {conv.unreadCount > 0 && (
                            <Badge
                              colorPalette="purple"
                              rounded="full"
                              fontSize="xs"
                              minW="20px"
                              h="20px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              {conv.unreadCount}
                            </Badge>
                          )}
                        </HStack>
                        <Text
                          fontSize="xs"
                          color={textColor}
                          lineClamp={1}
                          fontWeight={conv.unreadCount > 0 ? 'semibold' : 'normal'}
                        >
                          {conv.lastMessage}
                        </Text>
                        <Text fontSize="2xs" color="gray.400">
                          {formatTimestamp(conv.updatedAt)}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </Box>
            </Collapsible.Content>
          </Collapsible.Root>
        </Box>
      );
    });
  }, [
    borderColor,
    conversationsGroupedByEvent,
    expandedEventoIds,
    formatTimestamp,
    isUserOnline,
    selectedBg,
    selectedConversation?.id,
    textColor,
    toggleEventoExpanded,
  ]);

  // Formatear hora del mensaje
  const formatMessageTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  // Obtener nombre del perfil del remitente
  const getSenderName = (message) => {
    const sender = message.sender;
    if (!sender) return 'Usuario';
    return sender.banda?.nombre || sender.cofradia?.nombre || 'Usuario';
  };

  // Verificar si alguien está escribiendo
  const isOtherUserTyping = () => {
    if (!selectedConversation) return false;
    return Object.entries(typingUsers).some(([id, isTyping]) => 
      id !== user?.id && isTyping
    );
  };

  // Cargar acuerdo activo de la conversación
  const loadActiveAcuerdo = useCallback(async (conversacionId) => {
    try {
      setIsLoadingAcuerdo(true);
      const response = await acuerdosAPI.getActive(conversacionId);
      setActiveAcuerdo(response.data);
      
      // Verificar si hay otros acuerdos activos en el mismo evento
      const conversation = conversations.find(c => c.id === conversacionId);
      if (conversation?.evento?.id && userType === 'cofradia') {
        // Buscar otras conversaciones del mismo evento
        const otherConversationsWithSameEvent = conversations.filter(
          c => c.evento?.id === conversation.evento.id && c.id !== conversacionId
        );
        
        // Verificar si alguna de estas conversaciones tiene un acuerdo activo
        let hasOther = false;
        for (const otherConv of otherConversationsWithSameEvent) {
          try {
            const otherAcuerdoResponse = await acuerdosAPI.getActive(otherConv.id);
            const otherAcuerdo = otherAcuerdoResponse.data;
            if (otherAcuerdo && (otherAcuerdo.status === 'PENDING' || otherAcuerdo.status === 'ACCEPTED')) {
              hasOther = true;
              break;
            }
          } catch (err) {
            // No hay acuerdo activo en esta conversación
          }
        }
        setHasOtherActiveAcuerdo(hasOther);
      } else {
        setHasOtherActiveAcuerdo(false);
      }
    } catch (error) {
      console.error('Error cargando acuerdo:', error);
      setActiveAcuerdo(null);
      setHasOtherActiveAcuerdo(false);
    } finally {
      setIsLoadingAcuerdo(false);
    }
  }, [conversations, userType]);

  // Cargar acuerdo cuando cambia la conversación
  useEffect(() => {
    if (selectedConversation?.id) {
      loadActiveAcuerdo(selectedConversation.id);
    } else {
      setActiveAcuerdo(null);
    }
  }, [selectedConversation?.id, loadActiveAcuerdo]);

  const handleAcuerdoAdjuntosInputChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    setAcuerdoAdjuntosFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (next.length >= MAX_ACUERDO_ADJUNTOS_UI) break;
        next.push(f);
      }
      return next;
    });
  };

  const handleAcuerdoNewAdjuntosInputChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    setAcuerdoNewAdjuntosFiles((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (next.length >= MAX_ACUERDO_ADJUNTOS_UI) break;
        next.push(f);
      }
      return next;
    });
  };

  const handleUploadNewAdjuntosToActiveAcuerdo = async () => {
    if (!activeAcuerdo?.id) return;
    if (activeAcuerdo.status !== 'PENDING') return;
    if (acuerdoNewAdjuntosFiles.length === 0) return;
    if (!acuerdoNewAdjuntosAceptaDisclaimer) {
      toaster.create({
        title: 'Aviso legal obligatorio',
        description: 'Debes aceptar el aviso legal antes de adjuntar documentos.',
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }

    setIsSubmittingAcuerdo(true);
    try {
      const up = await acuerdosAPI.uploadAdjuntos(activeAcuerdo.id, acuerdoNewAdjuntosFiles, { aceptaDisclaimer: true });
      setActiveAcuerdo((prev) =>
        prev ? { ...prev, adjuntosInfo: up.data?.adjuntosInfo ?? prev.adjuntosInfo } : prev
      );
      setAcuerdoNewAdjuntosFiles([]);
      setAcuerdoNewAdjuntosAceptaDisclaimer(false);
      toaster.create({
        title: 'Documentos adjuntados',
        description: 'Los archivos ya están disponibles en el detalle del acuerdo.',
        type: 'success',
        duration: 4000,
        closable: true,
      });
    } catch (error) {
      console.error('Error subiendo adjuntos del acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudieron subir los documentos.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsSubmittingAcuerdo(false);
    }
  };

  const canAdvanceAcuerdoCreateStep = useCallback(
    (stepIdx) => {
      if (stepIdx === 0) return true;
      if (stepIdx === 1) return acuerdoDescription.trim().length > 0;
      if (stepIdx === 2) return true;
      if (stepIdx === 3) return acuerdoDescription.trim().length > 0 && acuerdoAceptaDisclaimer;
      return true;
    },
    [acuerdoDescription, acuerdoAceptaDisclaimer],
  );

  // Crear solicitud de acuerdo
  const handleCreateAcuerdo = async () => {
    if (!selectedConversation) return;
    if (!acuerdoAceptaDisclaimer) {
      toaster.create({
        title: 'Aviso legal obligatorio',
        description: 'Debes aceptar el aviso legal para enviar la propuesta de acuerdo.',
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }
    
    setIsSubmittingAcuerdo(true);
    try {
      const response = await acuerdosAPI.create({
        conversacionId: selectedConversation.id,
        descripcion: acuerdoDescription || undefined,
        fecha: selectedConversation.evento?.fecha || undefined,
        notas: acuerdoNotas || undefined,
        aceptaDisclaimer: true,
      });

      let acuerdo = response.data;
      let uploadedAdjuntosOk = true;
      if (acuerdoAdjuntosFiles.length > 0 && acuerdo?.id) {
        try {
          const up = await acuerdosAPI.uploadAdjuntos(acuerdo.id, acuerdoAdjuntosFiles, { aceptaDisclaimer: true });
          acuerdo = { ...acuerdo, adjuntosInfo: up.data?.adjuntosInfo ?? acuerdo.adjuntosInfo };
        } catch (uploadErr) {
          console.error('Error subiendo adjuntos del acuerdo:', uploadErr);
          uploadedAdjuntosOk = false;
        }
      }

      setActiveAcuerdo(acuerdo);
      
      // Enviar mensaje al chat notificando la propuesta
      let mensajePropuesta = '🤝 He propuesto un acuerdo para este evento.';
      if (acuerdoDescription) {
        mensajePropuesta += `\n\n📋 Descripción: ${acuerdoDescription}`;
      }
      if (acuerdoNotas) {
        mensajePropuesta += `\n\n📝 Notas: ${acuerdoNotas}`;
      }
      if (acuerdoAdjuntosFiles.length > 0 && uploadedAdjuntosOk) {
        mensajePropuesta += `\n\n📎 ${acuerdoAdjuntosFiles.length} archivo(s) adjunto(s). Ver detalles del acuerdo.`;
      }
      
      if (isConnected) {
        try {
          await socketSendMessage(selectedConversation.id, mensajePropuesta);
        } catch {
          await mensajesAPI.sendMensaje(selectedConversation.id, mensajePropuesta);
        }
      } else {
        await mensajesAPI.sendMensaje(selectedConversation.id, mensajePropuesta);
      }
      
      onAcuerdoModalClose();
      
      // Recargar acuerdo para actualizar hasOtherActiveAcuerdo
      await loadActiveAcuerdo(selectedConversation.id);
      
      toaster.create({
        title: '¡Propuesta enviada!',
        description:
          acuerdoAdjuntosFiles.length > 0 && !uploadedAdjuntosOk
            ? `La propuesta se envió a ${selectedConversation.name}, pero no se pudieron subir los adjuntos. Intenta subirlos de nuevo si el acuerdo sigue pendiente (contacta con soporte si persiste).`
            : `Se ha enviado la propuesta de acuerdo a ${selectedConversation.name}. ${selectedConversation.evento ? 'El evento permanecerá pendiente hasta que la banda acepte.' : ''}`,
        type: acuerdoAdjuntosFiles.length > 0 && !uploadedAdjuntosOk ? 'warning' : 'success',
        duration: 6000,
        closable: true,
      });
    } catch (error) {
      console.error('Error creando acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo enviar la solicitud de acuerdo.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsSubmittingAcuerdo(false);
    }
  };

  // Responder a solicitud de acuerdo
  const handleRespondAcuerdo = async (accept) => {
    if (!activeAcuerdo || !selectedConversation) return;
    if (accept && !aceptaDisclaimerAceptarAcuerdo) {
      toaster.create({
        title: 'Aviso legal obligatorio',
        description: 'Debes aceptar el aviso legal antes de aceptar el acuerdo.',
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }
    if (isConfirmador && activeAcuerdo.status === 'PENDING' && eventoYaEmpezo) {
      toaster.create({
        title: 'El evento ya ha pasado',
        description: 'Ya no puedes aceptar ni rechazar el acuerdo porque la hora de inicio del evento ya pasó.',
        type: 'warning',
        duration: 6000,
        closable: true,
      });
      return;
    }
    
    setIsSubmittingAcuerdo(true);
    try {
      const response = await acuerdosAPI.respond(activeAcuerdo.id, {
        accept,
        respuestaNotas: respuestaNotas || undefined,
      });
      
      setActiveAcuerdo(response.data);
      
      // Enviar mensaje al chat notificando la respuesta
      let mensajeRespuesta = '';
      if (accept) {
        mensajeRespuesta = '✅ He aceptado el acuerdo.';
        if (respuestaNotas) {
          mensajeRespuesta += `\n\n📝 Notas: ${respuestaNotas}`;
        }
      } else {
        mensajeRespuesta = '❌ He rechazado el acuerdo.';
        if (respuestaNotas) {
          mensajeRespuesta += `\n\n📝 Motivo: ${respuestaNotas}`;
        }
      }
      
      if (isConnected) {
        try {
          await socketSendMessage(selectedConversation.id, mensajeRespuesta);
        } catch {
          await mensajesAPI.sendMensaje(selectedConversation.id, mensajeRespuesta);
        }
      } else {
        await mensajesAPI.sendMensaje(selectedConversation.id, mensajeRespuesta);
      }
      
      onRespondModalClose();
      onRejectModalClose();
      setRespuestaNotas('');
      setAceptaDisclaimerAceptarAcuerdo(false);
      
      // Recargar acuerdo para actualizar hasOtherActiveAcuerdo
      await loadActiveAcuerdo(selectedConversation.id);
      
      toaster.create({
        title: accept ? 'Acuerdo aceptado' : 'Acuerdo rechazado',
        description: accept
          ? userType === 'banda'
            ? 'El evento queda confirmado. La fecha se ha reservado en tu calendario de disponibilidad y tienes una nueva actuación en Gestión (Actuaciones).'
            : '¡Felicidades! El acuerdo ha sido confirmado.'
          : 'Has rechazado la solicitud de acuerdo.',
        type: accept ? 'success' : 'info',
        duration: accept && userType === 'banda' ? 8000 : 5000,
      });
    } catch (error) {
      console.error('Error respondiendo acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo procesar la respuesta',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmittingAcuerdo(false);
    }
  };

  // Cancelar solicitud de acuerdo
  const handleCancelAcuerdo = async () => {
    if (!activeAcuerdo || !selectedConversation) return;
    
    setIsSubmittingAcuerdo(true);
    try {
      await acuerdosAPI.cancel(activeAcuerdo.id);
      
      // Enviar mensaje al chat notificando la cancelación
      const mensajeCancelacion = '🚫 He cancelado la propuesta de acuerdo.';
      
      if (isConnected) {
        try {
          await socketSendMessage(selectedConversation.id, mensajeCancelacion);
        } catch {
          await mensajesAPI.sendMensaje(selectedConversation.id, mensajeCancelacion);
        }
      } else {
        await mensajesAPI.sendMensaje(selectedConversation.id, mensajeCancelacion);
      }
      
      setActiveAcuerdo(null);
      
      // Recargar acuerdo para actualizar hasOtherActiveAcuerdo
      await loadActiveAcuerdo(selectedConversation.id);
      
      toaster.create({
        title: 'Solicitud cancelada',
        description: 'Has cancelado la solicitud de acuerdo.',
        type: 'info',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error cancelando acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo cancelar la solicitud',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmittingAcuerdo(false);
    }
  };

  // Rescindir acuerdo confirmado
  const handleRescindAcuerdo = async () => {
    if (!activeAcuerdo || !selectedConversation) return;

    setIsSubmittingAcuerdo(true);
    try {
      await acuerdosAPI.rescind(activeAcuerdo.id, rescindReason || undefined);
      
      // Enviar mensaje al chat notificando la rescisión
      const mensajeRescision = rescindReason 
        ? `⚠️ He rescindido el acuerdo.\n\nMotivo: ${rescindReason}` 
        : `⚠️ He rescindido el acuerdo.`;
      
      if (isConnected) {
        try {
          await socketSendMessage(selectedConversation.id, mensajeRescision);
        } catch {
          await mensajesAPI.sendMensaje(selectedConversation.id, mensajeRescision);
        }
      } else {
        await mensajesAPI.sendMensaje(selectedConversation.id, mensajeRescision);
      }
      
      setActiveAcuerdo(null);
      onRescindModalClose();
      setRescindReason('');

      // Recargar acuerdo para actualizar hasOtherActiveAcuerdo
      await loadActiveAcuerdo(selectedConversation.id);

      toaster.create({
        title: 'Acuerdo rescindido',
        description: 'El acuerdo ha sido rescindido. Se ha notificado a la otra parte.',
        type: 'warning',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error rescindiendo acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'No se pudo rescindir el acuerdo',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmittingAcuerdo(false);
    }
  };

  // Manejar apertura del modal de reseña
  const handleOpenReviewModal = () => {
    if (!selectedConversation?.evento) return;
    
    // Estructurar el evento con objetos anidados (igual que en Events.jsx)
    const eventoForReview = {
      id: selectedConversation.evento.id,
      nombre: selectedConversation.evento.nombre,
      fecha: selectedConversation.evento.fecha,
      ubicacion: selectedConversation.evento.ubicacion,
      bandaId: selectedConversation.evento.bandaId,
      cofradiaId: selectedConversation.evento.cofradiaId,
    };

    // Agregar objeto banda si existe
    if (selectedConversation.evento.bandaName) {
      eventoForReview.banda = {
        id: selectedConversation.evento.bandaId,
        nombre: selectedConversation.evento.bandaName,
        avatar: selectedConversation.evento.bandaAvatar,
      };
    }

    // Agregar objeto cofradía si existe
    if (selectedConversation.evento.cofradiaName) {
      eventoForReview.cofradia = {
        id: selectedConversation.evento.cofradiaId,
        nombre: selectedConversation.evento.cofradiaName,
        avatar: selectedConversation.evento.cofradiaAvatar,
      };
    }

    setEventoParaResenar(eventoForReview);
    onReviewModalOpen();
  };

  // Manejar envío de reseña
  const handleReviewSubmitted = async () => {
    if (!selectedConversation) return;
    
    try {
      // Marcar que ya existe reseña para este evento
      setHasReviewForEvent(true);
      
      // Enviar notificación al chat
      const myProfileName = profile?.nombre || (userType === 'banda' ? 'La banda' : 'La cofradía');
      const mensajeResena = userType === 'cofradia' 
        ? `⭐ ${myProfileName} te ha dejado una reseña sobre el evento. ¡Gracias por tu trabajo!`
        : `⭐ ${myProfileName} te ha dejado una reseña sobre el evento. ¡Gracias por vuestra colaboración!`;
      
      if (isConnected) {
        try {
          await socketSendMessage(selectedConversation.id, mensajeResena);
        } catch {
          await mensajesAPI.sendMensaje(selectedConversation.id, mensajeResena);
        }
      } else {
        await mensajesAPI.sendMensaje(selectedConversation.id, mensajeResena);
      }
      
      toaster.create({
        title: 'Reseña enviada',
        description: 'Se ha notificado al otro usuario de tu reseña.',
        type: 'success',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error notificando reseña:', error);
    }
  };

  // Obtener nombre del perfil
  const getProfileName = (userData) => {
    if (!userData) return 'Usuario';
    const band = resolveBandaFromUser(userData);
    const cof = resolveCofradiaFromUser(userData);
    return band?.nombre || cof?.nombre || 'Usuario';
  };

  // Verificar si el usuario actual es el confirmador
  const isConfirmador = activeAcuerdo?.confirmadorId === user?.id;
  const eventoInicioMs = useMemo(() => {
    const ev = selectedConversation?.evento;
    if (!ev?.fecha) return 0;
    return getEventoInicioTimestampMs({
      date: String(ev.fecha).split('T')[0],
      timeStart: ev.horaInicio || ev.hora || '',
      time: ev.hora || '',
      horaInicio: ev.horaInicio || '',
      hora: ev.hora || '',
    });
  }, [selectedConversation?.evento]);
  const eventoYaEmpezo = !!eventoInicioMs && Date.now() >= eventoInicioMs;

  // Obtener el estado del acuerdo en formato legible
  const getAcuerdoStatusInfo = () => {
    if (!activeAcuerdo) return null;
    
    switch (activeAcuerdo.status) {
      case 'PENDING':
        return {
          color: 'yellow',
          icon: FaClock,
          text: isConfirmador 
            ? 'Tienes una solicitud de acuerdo pendiente' 
            : 'Esperando confirmación del acuerdo',
        };
      case 'ACCEPTED':
        return {
          color: 'green',
          icon: FaCheckCircle,
          text: '¡Acuerdo confirmado!',
        };
      case 'REJECTED':
        return {
          color: 'red',
          icon: FaTimesCircle,
          text: 'Acuerdo rechazado',
        };
      case 'CANCELLED':
        return {
          color: 'gray',
          icon: FaTimesCircle,
          text: 'Acuerdo cancelado',
        };
      case 'RESCINDED':
        return {
          color: 'orange',
          icon: FaTimesCircle,
          text: 'Acuerdo rescindido',
        };
      default:
        return null;
    }
  };

  return (
    <AppLayout userType={userType} userName={getPerfilDisplayName(profile, userType)}>
      <Box py={{ base: 4, md: 8 }}>
        <Container maxW="7xl">
          <Card.Root bg={cardBg} shadow="lg" rounded="2xl" h="calc(100vh - 200px)" minH="600px" overflow="hidden">
            <Card.Body p={0} h="full" overflow="hidden">
              <Flex h="full" overflow="hidden">
                {/* Lista de conversaciones */}
                <Box
                  w={{ base: 'full', md: '350px' }}
                  borderRight={{ base: 'none', md: '1px' }}
                  borderColor={borderColor}
                  display={{ base: selectedConversation ? 'none' : 'block', md: 'block' }}
                  h="full"
                  overflow="hidden"
                >
                  <Flex direction="column" h="full">
                    {/* Header con estado de conexión */}
                    <HStack p={4} borderBottom="1px" borderColor={borderColor} justify="space-between" data-tour="mensajes-header">
                      <Text fontWeight="bold" fontSize="lg">Mensajes</Text>
                      <HStack gap={2}>
                        <Icon 
                          as={FaWifi} 
                          color={isConnected ? 'green.500' : 'red.500'} 
                          w={4} 
                          h={4}
                        />
                        <Text fontSize="xs" color={isConnected ? 'green.500' : 'red.500'}>
                          {isConnected ? 'Conectado' : 'Desconectado'}
                        </Text>
                      </HStack>
                    </HStack>

                    {/* Búsqueda */}
                    <Box p={4} w="full" borderBottom="1px" borderColor={borderColor} flexShrink={0} data-tour="mensajes-buscar">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FaSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Buscar conversaciones..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          rounded="full"
                        />
                      </InputGroup>
                    </Box>

                    {/* Pestañas */}
                    <Tabs.Root 
                      value={tabsControlledValue(activeTab)} 
                      onValueChange={tabsOnValueChange(setActiveTab)} 
                      colorPalette="purple"
                      size="sm"
                      w="full"
                      flex={1}
                      display="flex"
                      flexDirection="column"
                      overflow="hidden"
                    >
                      <Tabs.List 
                        px={2} 
                        pt={2} 
                        borderBottom="2px" 
                        borderColor={borderColor}
                        flexWrap="wrap"
                        gap={1}
                        data-tour="mensajes-tabs"
                      >
                        <Tab value="0" 
                          fontWeight="600" 
                          fontSize="sm"
                          px={2}
                          _selected={{ color: 'purple.600', borderColor: 'purple.600' }}
                        >
                          <HStack gap={1}>
                            <Text>Activos</Text>
                            {conversations.filter((c) => eventoConversacionActiva(c.evento)).length > 0 && (
                              <Badge colorPalette="purple" rounded="full" fontSize="xs">
                                {conversations.filter((c) => eventoConversacionActiva(c.evento)).length}
                              </Badge>
                            )}
                          </HStack>
                        </Tab>
                        <Tab value="1" 
                          fontWeight="600" 
                          fontSize="sm"
                          px={2}
                          _selected={{ color: 'green.600', borderColor: 'green.600' }}
                        >
                          <HStack gap={1}>
                            <Text>Finalizados</Text>
                            {conversations.filter((c) => eventoConversacionFinalizada(c.evento)).length > 0 && (
                              <Badge colorPalette="green" rounded="full" fontSize="xs">
                                {conversations.filter((c) => eventoConversacionFinalizada(c.evento)).length}
                              </Badge>
                            )}
                          </HStack>
                        </Tab>
                        <Tab value="2" 
                          fontWeight="600" 
                          fontSize="sm"
                          px={2}
                          _selected={{ color: 'red.600', borderColor: 'red.600' }}
                        >
                          <HStack gap={1}>
                            <Text>Cancelados</Text>
                            {conversations.filter(c => c.evento?.status?.toLowerCase() === 'cancelled').length > 0 && (
                              <Badge colorPalette="red" rounded="full" fontSize="xs">
                                {conversations.filter(c => c.evento?.status?.toLowerCase() === 'cancelled').length}
                              </Badge>
                            )}
                          </HStack>
                        </Tab>
                      </Tabs.List>

                      <TabPanels flex={1} overflow="hidden">
                        <TabPanel value="0" p={0} h="full" overflow="auto">
                          {/* Lista de conversaciones */}
                          <VStack gap={0} w="full" overflow="auto" overflowX="hidden">
                      {isLoading ? (
                        <Flex flex={1} align="center" justify="center" py={8}>
                          <Spinner size="lg" color="purple.500" />
                        </Flex>
                      ) : filteredConversations.length === 0 ? (
                        <Flex flex={1} align="center" justify="center" py={8}>
                          <VStack gap={3}>
                            <Icon as={FaComments} w={12} h={12} color="gray.300" />
                            <Text color={textColor} textAlign="center">
                              No tienes conversaciones
                            </Text>
                          </VStack>
                        </Flex>
                      ) : userType === 'cofradia' && conversationsGroupedByEvent.length > 0 ? (
                        <VStack gap={0} w="full">
                          {renderCofradiaEventGroups('purple')}
                        </VStack>
                      ) : (
                        /* Vista normal para bandas */
                        filteredConversations.map((conv) => (
                          <Box
                            key={conv.id}
                            id={`conversation-${conv.id}`}
                            w="full"
                            p={4}
                            cursor="pointer"
                            bg={selectedConversation?.id === conv.id ? selectedBg : 'transparent'}
                            borderBottom="1px"
                            borderColor={borderColor}
                            _hover={{ bg: selectedConversation?.id === conv.id ? selectedBg : 'gray.50' }}
                            onClick={() => setSelectedConversation(conv)}
                            transition="background 0.2s"
                          >
                            <HStack gap={3} align="start">
                              <Box position="relative">
                                <Avatar.Root size="md"><Avatar.Fallback name={conv.name} /><Avatar.Image src={conv.avatar} /></Avatar.Root>
                                {isUserOnline(conv.otherUserId) && (
                                  <Box
                                    position="absolute"
                                    bottom={0}
                                    right={0}
                                    w={3}
                                    h={3}
                                    bg="green.500"
                                    border="2px solid white"
                                    rounded="full"
                                  />
                                )}
                              </Box>
                              <VStack gap={1} align="start" flex={1} minW={0}>
                                <HStack w="full" justify="space-between">
                                  <Text 
                                    fontWeight={conv.unreadCount > 0 ? "bold" : "bold"} 
                                    fontSize="sm" 
                                    lineClamp={1}
                                  >
                                    {conv.name}
                                  </Text>
                                  {conv.unreadCount > 0 && (
                                    <Badge 
                                      colorPalette="purple" 
                                      rounded="full" 
                                      fontSize="xs"
                                      minW="20px"
                                      h="20px"
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                    >
                                      {conv.unreadCount}
                                    </Badge>
                                  )}
                                </HStack>
                                {conv.evento && (
                                  <HStack gap={1}>
                                    <Icon as={FaCalendarAlt} color="purple.500" boxSize={3} />
                                    <Text fontSize="xs" color="purple.600" fontWeight="medium" lineClamp={1}>
                                      {conv.evento.nombre}
                                    </Text>
                                  </HStack>
                                )}
                                <Text 
                                  fontSize="xs" 
                                  color={textColor} 
                                  lineClamp={1}
                                  fontWeight={conv.unreadCount > 0 ? "semibold" : "normal"}
                                >
                                  {conv.lastMessage}
                                </Text>
                                <Text fontSize="2xs" color="gray.400">
                                  {formatTimestamp(conv.updatedAt)}
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>
                        ))
                      )}
                          </VStack>
                        </TabPanel>
                        
                        {/* Pestaña de Finalizados */}
                        <TabPanel value="1" p={0} h="full" overflow="auto">
                          <VStack gap={0} w="full" overflow="auto" overflowX="hidden">
                            {isLoading ? (
                              <Flex flex={1} align="center" justify="center" py={8}>
                                <Spinner size="lg" color="purple.500" />
                              </Flex>
                            ) : filteredConversations.length === 0 ? (
                              <Flex flex={1} align="center" justify="center" py={8}>
                                <VStack gap={3}>
                                  <Icon as={FaComments} w={12} h={12} color="gray.300" />
                                  <Text color={textColor} textAlign="center">
                                    No hay eventos finalizados
                                  </Text>
                                </VStack>
                              </Flex>
                            ) : userType === 'cofradia' && conversationsGroupedByEvent.length > 0 ? (
                              <VStack gap={0} w="full">
                                {renderCofradiaEventGroups('green')}
                              </VStack>
                            ) : (
                              /* Vista normal para bandas */
                              filteredConversations.map((conv) => (
                                <Box
                                  key={conv.id}
                                  w="full"
                                  p={4}
                                  cursor="pointer"
                                  bg={selectedConversation?.id === conv.id ? selectedBg : 'transparent'}
                                  borderBottom="1px"
                                  borderColor={borderColor}
                                  _hover={{ bg: selectedConversation?.id === conv.id ? selectedBg : 'gray.50' }}
                                  onClick={() => setSelectedConversation(conv)}
                                  transition="background 0.2s"
                                >
                                  <HStack gap={3} align="start">
                                    <Avatar.Root size="md"><Avatar.Fallback name={conv.name} /><Avatar.Image src={conv.avatar} /></Avatar.Root>
                                    <VStack gap={1} align="start" flex={1} minW={0}>
                                      <HStack w="full" justify="space-between">
                                        <Text fontWeight="bold" fontSize="sm" lineClamp={1}>{conv.name}</Text>
                                        {conv.unreadCount > 0 && (
                                          <Badge 
                                            colorPalette="purple" 
                                            rounded="full" 
                                            fontSize="xs"
                                            minW="20px"
                                            h="20px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            {conv.unreadCount}
                                          </Badge>
                                        )}
                                      </HStack>
                                      {conv.evento && (
                                        <HStack gap={1}>
                                          <Icon as={FaCalendarAlt} color="purple.500" boxSize={3} />
                                          <Text fontSize="xs" color="purple.600" fontWeight="medium" lineClamp={1}>
                                            {conv.evento.nombre}
                                          </Text>
                                        </HStack>
                                      )}
                                      <Text fontSize="xs" color={textColor} lineClamp={1} fontWeight={conv.unreadCount > 0 ? "semibold" : "normal"}>
                                        {conv.lastMessage}
                                      </Text>
                                      <Text fontSize="2xs" color="gray.400">{formatTimestamp(conv.updatedAt)}</Text>
                                    </VStack>
                                  </HStack>
                                </Box>
                              ))
                            )}
                          </VStack>
                        </TabPanel>
                        
                        {/* Pestaña de Cancelados */}
                        <TabPanel value="2" p={0} h="full" overflow="auto">
                          <VStack gap={0} w="full" overflow="auto" overflowX="hidden">
                            {isLoading ? (
                              <Flex flex={1} align="center" justify="center" py={8}>
                                <Spinner size="lg" color="purple.500" />
                              </Flex>
                            ) : filteredConversations.length === 0 ? (
                              <Flex flex={1} align="center" justify="center" py={8}>
                                <VStack gap={3}>
                                  <Icon as={FaComments} w={12} h={12} color="gray.300" />
                                  <Text color={textColor} textAlign="center">
                                    No hay eventos cancelados
                                  </Text>
                                </VStack>
                              </Flex>
                            ) : userType === 'cofradia' && conversationsGroupedByEvent.length > 0 ? (
                              <VStack gap={0} w="full">
                                {renderCofradiaEventGroups('red')}
                              </VStack>
                            ) : (
                              /* Vista normal para bandas */
                              filteredConversations.map((conv) => (
                                <Box
                                  key={conv.id}
                                  w="full"
                                  p={4}
                                  cursor="pointer"
                                  bg={selectedConversation?.id === conv.id ? selectedBg : 'transparent'}
                                  borderBottom="1px"
                                  borderColor={borderColor}
                                  _hover={{ bg: selectedConversation?.id === conv.id ? selectedBg : 'gray.50' }}
                                  onClick={() => setSelectedConversation(conv)}
                                  transition="background 0.2s"
                                >
                                  <HStack gap={3} align="start">
                                    <Avatar.Root size="md"><Avatar.Fallback name={conv.name} /><Avatar.Image src={conv.avatar} /></Avatar.Root>
                                    <VStack gap={1} align="start" flex={1} minW={0}>
                                      <HStack w="full" justify="space-between">
                                        <Text fontWeight="bold" fontSize="sm" lineClamp={1}>{conv.name}</Text>
                                        {conv.unreadCount > 0 && (
                                          <Badge 
                                            colorPalette="purple" 
                                            rounded="full" 
                                            fontSize="xs"
                                            minW="20px"
                                            h="20px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                          >
                                            {conv.unreadCount}
                                          </Badge>
                                        )}
                                      </HStack>
                                      {conv.evento && (
                                        <HStack gap={1}>
                                          <Icon as={FaCalendarAlt} color="purple.500" boxSize={3} />
                                          <Text fontSize="xs" color="purple.600" fontWeight="medium" lineClamp={1}>
                                            {conv.evento.nombre}
                                          </Text>
                                        </HStack>
                                      )}
                                      <Text fontSize="xs" color={textColor} lineClamp={1} fontWeight={conv.unreadCount > 0 ? "semibold" : "normal"}>
                                        {conv.lastMessage}
                                      </Text>
                                      <Text fontSize="2xs" color="gray.400">{formatTimestamp(conv.updatedAt)}</Text>
                                    </VStack>
                                  </HStack>
                                </Box>
                              ))
                            )}
                          </VStack>
                        </TabPanel>
                      </TabPanels>
                    </Tabs.Root>
                  </Flex>
                </Box>

                {/* Chat */}
                <Flex
                  flex={1}
                  minH={0}
                  direction="column"
                  display={{ base: selectedConversation ? 'flex' : 'none', md: 'flex' }}
                  h="full"
                  overflow="hidden"
                  data-tour="mensajes-chat"
                >
                  {selectedConversation ? (
                    <>
                      {/* Header del chat */}
                      <HStack
                        p={4}
                        borderBottom="1px"
                        borderColor={borderColor}
                        gap={3}
                        flexShrink={0}
                        flexWrap="wrap"
                        rowGap={2}
                      >
                        {/* Botón volver en móvil */}
                        <IconButton
                          variant="ghost"
                          display={{ base: 'flex', md: 'none' }}
                          onClick={() => setSelectedConversation(null)}
                          aria-label="Volver"><Icon as={FaComments} /></IconButton>
                        <Avatar.Root size="sm"><Avatar.Fallback name={selectedConversation.name} /><Avatar.Image src={selectedConversation.avatar} /></Avatar.Root>
                        <VStack gap={0} align="start" flex={1}>
                          <Text fontWeight="bold" fontSize="md">
                            {selectedConversation.name}
                          </Text>
                          {selectedConversation.evento && (
                            <HStack gap={1}>
                              <Icon as={FaCalendarAlt} color="purple.500" boxSize={3} />
                              <Text fontSize="xs" color="purple.600" fontWeight="medium">
                                {selectedConversation.evento.nombre}
                              </Text>
                            </HStack>
                          )}
                          <HStack gap={1}>
                            <Icon
                              as={FaCircle}
                              w={2}
                              h={2}
                              color={isUserOnline(selectedConversation.otherUserId) ? 'green.500' : 'gray.400'}
                            />
                            <Text fontSize="xs" color={textColor}>
                              {isOtherUserTyping() 
                                ? 'Escribiendo...' 
                                : isUserOnline(selectedConversation.otherUserId) 
                                  ? 'En línea' 
                                  : 'Desconectado'}
                            </Text>
                          </HStack>
                        </VStack>

                        {selectedConversation.evento?.id ? (
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="purple"
                            flexShrink={0}
                            onClick={() =>
                              navigate(
                                `/${userType}/eventos/${selectedConversation.evento.id}?desde=chat`,
                              )
                            }><Icon as={FaCalendarCheck} />Ver detalles del evento
                                                      </Button>
                        ) : null}
                        
                        {/* Botón de acuerdo - Solo para cofradías */}
                        {userType === 'cofradia' && (!activeAcuerdo || activeAcuerdo.status === 'REJECTED' || activeAcuerdo.status === 'CANCELLED' || activeAcuerdo.status === 'RESCINDED') ? (
                          // Solo mostrar botón si no hay otro acuerdo activo en el mismo evento
                          !hasOtherActiveAcuerdo ? (
                            <Tooltip content="Proponer acuerdo" showArrow>
                              <Button
                                colorPalette="purple"
                                size="sm"
                                onClick={onAcuerdoModalOpen}
                                display={{ base: 'none', sm: 'flex' }}><Icon as={FaHandshake} />Proponer Acuerdo
                                                              </Button>
                            </Tooltip>
                          ) : null
                        ) : activeAcuerdo && activeAcuerdo.status === 'ACCEPTED' ? (
                          <Badge colorPalette={eventoConversacionFinalizada(selectedConversation?.evento) ? 'blue' : 'green'} px={3} py={1} rounded="full">
                            <HStack gap={1}>
                              <Icon as={FaCheckCircle} />
                              <Text>{eventoConversacionFinalizada(selectedConversation?.evento) ? 'COMPLETADO' : 'Acuerdo Cerrado'}</Text>
                            </HStack>
                          </Badge>
                        ) : null}
                        
                        {/* Botón móvil - Solo para cofradías */}
                        {userType === 'cofradia' && (!activeAcuerdo || activeAcuerdo.status === 'REJECTED' || activeAcuerdo.status === 'CANCELLED' || activeAcuerdo.status === 'RESCINDED') && !hasOtherActiveAcuerdo && (
                          <IconButton
                            colorPalette="purple"
                            size="sm"
                            onClick={onAcuerdoModalOpen}
                            display={{ base: 'flex', sm: 'none' }}
                            aria-label="Proponer acuerdo"><Icon as={FaHandshake} /></IconButton>
                        )}
                      </HStack>

                      {/* Banner de estado del acuerdo */}
                      {activeAcuerdo && activeAcuerdo.status === 'PENDING' && (
                        <Alert.Root
                          status={isConfirmador && eventoYaEmpezo ? 'error' : (isConfirmador ? 'warning' : 'info')}
                          variant='subtle'
                          py={2}
                          px={4}
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator />
                          <Box flex={1}>
                            <Alert.Title fontSize="sm">
                              {isConfirmador 
                                ? `${getProfileName(activeAcuerdo.solicitante)} te propone cerrar un acuerdo`
                                : 'Solicitud de acuerdo enviada'}
                            </Alert.Title>
                            {isConfirmador && eventoYaEmpezo && (
                              <Alert.Description fontSize="xs" mt={1}>
                                El evento ya ha pasado, por lo que no se puede aceptar ni rechazar el acuerdo.
                              </Alert.Description>
                            )}
                            <HStack gap={2} mt={2} flexWrap="wrap">
                              <Button
                                size="sm"
                                colorPalette="purple"
                                variant="solid"
                                onClick={onAcuerdoDetailsOpen}
                                px={4}
                                rounded="full"><Icon as={FaInfoCircle} boxSize={4} />Ver detalles del acuerdo
                                                              </Button>
                            </HStack>
                          </Box>
                          {isConfirmador ? (
                            <HStack gap={2}>
                              <Button
                                size="xs"
                                colorPalette="green"
                                onClick={onRespondModalOpen}
                                loading={isSubmittingAcuerdo}
                                disabled={eventoYaEmpezo}><Icon as={FaCheck} />Aceptar
                                                              </Button>
                              <Button
                                size="xs"
                                colorPalette="red"
                                variant="outline"
                                onClick={onRejectModalOpen}
                                loading={isSubmittingAcuerdo}
                                disabled={eventoYaEmpezo}><Icon as={FaTimes} />Rechazar
                                                              </Button>
                            </HStack>
                          ) : (
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={onCancelModalOpen}
                              loading={isSubmittingAcuerdo}
                            >
                              Cancelar
                            </Button>
                          )}
                        </Alert.Root>
                      )}

                      {/* Banner de acuerdo aceptado o evento completado */}
                      {activeAcuerdo && activeAcuerdo.status === 'ACCEPTED' && (
                        <Alert.Root
                          status={eventoConversacionFinalizada(selectedConversation?.evento) ? 'info' : 'success'}
                          variant='subtle'
                          py={2}
                          px={4}
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator />
                          <Box flex={1}>
                            <Alert.Title fontSize="sm">
                              {eventoConversacionFinalizada(selectedConversation?.evento) ? '🎉 Evento completado' : '¡Acuerdo confirmado!'}
                            </Alert.Title>
                            <Alert.Description fontSize="xs">
                              {eventoConversacionFinalizada(selectedConversation?.evento) 
                                ? 'El evento ha finalizado. ¡Esperamos que haya ido muy bien!' 
                                : 'Ambas partes han aceptado los términos del acuerdo.'}
                            </Alert.Description>
                          </Box>
                          {!eventoConversacionFinalizada(selectedConversation?.evento) && (
                            <Button
                              size="xs"
                              colorPalette="red"
                              variant="outline"
                              onClick={onRescindModalOpen}
                            >
                              Rescindir
                            </Button>
                          )}
                        </Alert.Root>
                      )}

                      {/* Banner de acuerdo rescindido */}
                      {activeAcuerdo && activeAcuerdo.status === 'RESCINDED' && (
                        <Alert.Root
                          status="warning"
                          variant='subtle'
                          py={2}
                          px={4}
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator />
                          <Box flex={1}>
                            <Alert.Title fontSize="sm">Acuerdo rescindido</Alert.Title>
                            <Alert.Description fontSize="xs">
                              Este acuerdo ha sido rescindido.
                              {activeAcuerdo.rescindReason && ` Motivo: ${activeAcuerdo.rescindReason}`}
                            </Alert.Description>
                          </Box>
                        </Alert.Root>
                      )}

                      {/* Mensajes */}
                      <VStack
                        ref={messagesContainerRef}
                        flex={1}
                        minH={0}
                        p={4}
                        gap={4}
                        overflowY="auto"
                        overflowX="hidden"
                        align="stretch"
                      >
                        {isLoadingMessages ? (
                          <Flex flex={1} align="center" justify="center">
                            <Spinner size="lg" color="purple.500" />
                          </Flex>
                        ) : messages.length === 0 ? (
                          <Flex flex={1} align="center" justify="center">
                            <VStack gap={3}>
                              <Icon as={FaComments} w={12} h={12} color="gray.300" />
                              <Text color={textColor}>No hay mensajes aún</Text>
                              <Text fontSize="sm" color="gray.400">
                                ¡Envía el primer mensaje!
                              </Text>
                            </VStack>
                          </Flex>
                        ) : (
                          <>
                          {/* Indicador de carga de mensajes anteriores */}
                          {isLoadingOlder && (
                            <Flex justify="center" py={2}>
                              <Spinner size="sm" color="purple.400" />
                            </Flex>
                          )}
                          {hasMoreMessages && !isLoadingOlder && (
                            <Flex justify="center" py={1}>
                              <Text fontSize="xs" color="gray.400" cursor="pointer" onClick={loadOlderMessages} _hover={{ color: 'purple.400' }}>
                                Cargar mensajes anteriores
                              </Text>
                            </Flex>
                          )}
                          {messages.map((message) => (
                            <Flex
                              key={message.id}
                              justify={message.senderId === user?.id ? 'flex-end' : 'flex-start'}
                            >
                              <Box
                                maxW="70%"
                                bg={message.senderId === user?.id ? myMessageBg : messageBg}
                                color={message.senderId === user?.id ? 'white' : 'inherit'}
                                px={4}
                                py={2}
                                rounded="2xl"
                                roundedBottomLeft={message.senderId !== user?.id ? 'none' : '2xl'}
                                roundedBottomRight={message.senderId === user?.id ? 'none' : '2xl'}
                              >
                                <Text fontSize="sm">{message.content}</Text>
                                <HStack 
                                  gap={1} 
                                  justify={message.senderId === user?.id ? 'flex-end' : 'flex-start'}
                                  mt={1}
                                >
                                  <Text
                                    fontSize="2xs"
                                    color={message.senderId === user?.id ? 'whiteAlpha.700' : 'gray.500'}
                                  >
                                    {formatMessageTime(message.createdAt)}
                                  </Text>
                                  {message.senderId === user?.id && (
                                    <Icon 
                                      as={message.isRead ? FaCheckDouble : FaCheck} 
                                      boxSize={3}
                                      color={message.isRead ? 'blue.300' : 'whiteAlpha.700'}
                                    />
                                  )}
                                </HStack>
                              </Box>
                            </Flex>
                          ))}
                          </>
                        )}
                        
                        {/* Banner reseña cuando el evento ya finalizó (COMPLETED o pasó fecha+hora) */}
                        {activeAcuerdo && eventoConversacionFinalizada(selectedConversation?.evento) && (
                          <Card.Root 
                            bg={hasReviewForEvent 
                              ? useColorModeValue('green.50', 'green.900') 
                              : useColorModeValue('blue.50', 'blue.900')} 
                            shadow="md" 
                            rounded="xl"
                            borderWidth="2px"
                            borderColor={hasReviewForEvent ? 'green.400' : 'blue.400'}
                            mt={4}
                          >
                            <Card.Body>
                              {hasReviewForEvent ? (
                                // Mensaje de agradecimiento cuando ya dejó reseña
                                <VStack gap={4} align="stretch">
                                  <HStack gap={3}>
                                    <Icon as={FaStar} color="green.600" boxSize={6} />
                                    <VStack align="start" gap={0} flex={1}>
                                      <Heading size="sm" color="green.800">
                                        ¡Gracias por tu reseña! 🌟
                                      </Heading>
                                      <Text fontSize="sm" color="green.600">
                                        Tu opinión ha sido registrada
                                      </Text>
                                    </VStack>
                                  </HStack>

                                  <Separator borderColor="green.300" />

                                  <Text fontSize="sm" color="green.700">
                                    {userType === 'banda' 
                                      ? 'Tu reseña ayudará a otras cofradías a tomar mejores decisiones. ¡Gracias por contribuir a mejorar nuestra comunidad!'
                                      : 'Tu reseña ayudará a que esta banda pueda crecer y conseguir más oportunidades. ¡Gracias por contribuir a mejorar nuestra comunidad!'}
                                  </Text>

                                  <HStack justify="center" gap={2} pt={2}>
                                    <Icon as={FaCheckCircle} color="green.500" />
                                    <Text fontSize="xs" color="green.600" fontWeight="medium">
                                      Reseña enviada correctamente
                                    </Text>
                                  </HStack>
                                </VStack>
                              ) : (
                                // Banner original para dejar reseña
                                <VStack gap={4} align="stretch">
                                  {/* Encabezado */}
                                  <HStack gap={3}>
                                    <Icon as={FaCheckCircle} color="blue.600" boxSize={6} />
                                    <VStack align="start" gap={0} flex={1}>
                                      <Heading size="sm" color="blue.800">
                                        ¡Evento finalizado! 🎉
                                      </Heading>
                                      <Text fontSize="sm" color="blue.600">
                                        ¿Qué tal ha ido todo?
                                      </Text>
                                    </VStack>
                                  </HStack>

                                  <Separator borderColor="blue.300" />

                                  {/* Texto motivador */}
                                  <Text fontSize="sm" color="blue.700">
                                    Nos encantaría conocer tu experiencia. Tu opinión es muy valiosa y ayuda a mejorar la comunidad. 
                                    ¡Comparte cómo ha sido trabajar {userType === 'banda' ? 'con esta cofradía' : 'con esta banda'}!
                                  </Text>

                                  {/* Botón de reseña */}
                                  <Button
                                    colorPalette="blue"
                                    size="md"
                                    onClick={handleOpenReviewModal}
                                    fontWeight="600"
                                    _hover={{ transform: 'scale(1.02)' }}
                                    transition="all 0.2s"><Icon as={FaStar} />Dejar una reseña
                                                                      <Icon as={FaSmile} /></Button>

                                  <Text fontSize="xs" color="blue.500" textAlign="center">
                                    Tu reseña ayuda a otros a tomar mejores decisiones
                                  </Text>
                                </VStack>
                              )}
                            </Card.Body>
                          </Card.Root>
                        )}
                        
                        {/* Indicador de escribiendo */}
                        {isOtherUserTyping() && (
                          <Flex justify="flex-start">
                            <Box
                              bg={messageBg}
                              px={4}
                              py={2}
                              rounded="2xl"
                              roundedBottomLeft="none"
                            >
                              <HStack gap={1}>
                                <Box w={2} h={2} bg="gray.400" rounded="full" animation="bounce 1s infinite" />
                                <Box w={2} h={2} bg="gray.400" rounded="full" animation="bounce 1s infinite 0.2s" />
                                <Box w={2} h={2} bg="gray.400" rounded="full" animation="bounce 1s infinite 0.4s" />
                              </HStack>
                            </Box>
                          </Flex>
                        )}
                      </VStack>

                      {/* Input de mensaje */}
                      <HStack p={4} borderTop="1px" borderColor={borderColor} gap={2} flexShrink={0}>
                        <Input
                          placeholder="Escribe un mensaje..."
                          value={messageText}
                          onChange={(e) => {
                            setMessageText(e.target.value);
                            handleTyping();
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          rounded="full"
                          size="md"
                        />
                        <IconButton
                          colorPalette="purple"
                          rounded="full"
                          onClick={handleSendMessage}
                          size="md"
                          disabled={!messageText.trim()}
                          loading={isSending}><Icon as={FaPaperPlane} /></IconButton>
                      </HStack>
                    </>
                  ) : (
                    <Flex flex={1} align="center" justify="center">
                      <VStack gap={4}>
                        <Icon as={FaComments} w={16} h={16} color="gray.300" />
                        <Text color={textColor}>
                          Selecciona una conversación para comenzar
                        </Text>
                      </VStack>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            </Card.Body>
          </Card.Root>
        </Container>
      </Box>

      {/* Modal para crear solicitud de acuerdo (asistente por pasos) */}
      <Dialog.Root open={isAcuerdoModalOpen} size='xl' placement='center' onOpenChange={e => {
        if (!e.open) {
          onAcuerdoModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="purple.500" color="white" roundedTop="md" pb={4}>
                <VStack align="stretch" gap={3}>
                  <HStack justify="space-between" align="flex-start" gap={3}>
                    <HStack gap={3}>
                      <Icon as={FaHandshake} w={6} h={6} />
                      <Text fontWeight="700" fontSize="lg">
                        Proponer acuerdo
                      </Text>
                    </HStack>
                    <Badge
                      colorPalette="purple"
                      variant="solid"
                      rounded="full"
                      px={3}
                      py={1}
                      bg="whiteAlpha.900"
                      color="purple.700"
                    >
                      Paso {acuerdoCreateStep + 1} / {ACUERDO_CREATE_STEPS.length}
                    </Badge>
                  </HStack>
                  <Box>
                    <Text fontSize="sm" fontWeight="700">
                      {ACUERDO_CREATE_STEPS[acuerdoCreateStep]?.title}
                    </Text>
                    <Text fontSize="xs" color="purple.100">
                      {ACUERDO_CREATE_STEPS[acuerdoCreateStep]?.subtitle}
                    </Text>
                  </Box>
                  <Progress.Root
                    value={(acuerdoCreateStep / (ACUERDO_CREATE_STEPS.length - 1)) * 100}
                    size="sm"
                    colorPalette="whiteAlpha"
                    bg="whiteAlpha.300"
                    rounded="full">
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                </VStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                {selectedConversation && (
                  <VStack gap={6} align="stretch">
                    {acuerdoCreateStep === 0 && (
                      <Box
                        bg="green.50"
                        p={5}
                        rounded="xl"
                        borderWidth="2px"
                        borderColor="green.200"
                      >
                        <VStack gap={4} align="stretch">
                          <HStack gap={3}>
                            <Avatar.Root size="lg"><Avatar.Fallback name={selectedConversation.name}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={selectedConversation.avatar} /></Avatar.Root>
                            <VStack align="start" gap={1} flex={1}>
                              <Text fontSize="xl" fontWeight="bold" color="green.800">
                                {selectedConversation.name}
                              </Text>
                              {selectedConversation.evento && (
                                <>
                                  <HStack gap={1}>
                                    <Icon as={FaCalendarCheck} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="green.700">
                                      {selectedConversation.evento.nombre}
                                    </Text>
                                  </HStack>
                                  <HStack gap={1}>
                                    <Icon as={FaCalendarAlt} color="green.600" boxSize={4} />
                                    <Text fontSize="sm" color="green.700">
                                      {new Date(selectedConversation.evento.fecha).toLocaleDateString(
                                        'es-ES',
                                        {
                                          weekday: 'long',
                                          day: 'numeric',
                                          month: 'long',
                                          year: 'numeric',
                                        },
                                      )}
                                    </Text>
                                  </HStack>
                                  {(() => {
                                    const { inicio, fin } = horasEventoConversacion(
                                      selectedConversation.evento,
                                    );
                                    return (
                                      <HStack gap={1} align="start">
                                        <Icon as={FaClock} color="green.600" boxSize={4} mt={0.5} />
                                        <VStack align="start" gap={0.5}>
                                          <Text fontSize="sm" color="green.700">
                                            <Text as="span" fontWeight="semibold" color="green.900">
                                              Hora inicio:{' '}
                                            </Text>
                                            {inicio || 'No indicada'}
                                          </Text>
                                          <Text fontSize="sm" color="green.700">
                                            <Text as="span" fontWeight="semibold" color="green.900">
                                              Hora fin:{' '}
                                            </Text>
                                            {fin || 'No indicada'}
                                          </Text>
                                        </VStack>
                                      </HStack>
                                    );
                                  })()}
                                  {selectedConversation.evento.ubicacion && (
                                    <HStack gap={1}>
                                      <Icon as={FaMapMarkerAlt} color="green.600" boxSize={4} />
                                      <Text fontSize="sm" color="green.700">
                                        {selectedConversation.evento.ubicacion}
                                      </Text>
                                    </HStack>
                                  )}
                                </>
                              )}
                            </VStack>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                    {acuerdoCreateStep === 1 && (
                      <VStack gap={4} align="stretch">
                        <Field.Root required>
                          <Field.Label fontSize="sm">Descripción del acuerdo</Field.Label>
                          <Textarea
                            placeholder="Describe los términos del acuerdo (ej: Actuación para la procesión del Viernes Santo...)"
                            value={acuerdoDescription}
                            onChange={(e) => setAcuerdoDescription(e.target.value)}
                            rows={5}
                            borderColor="purple.200"
                            _hover={{ borderColor: 'purple.300' }}
                            _focus={{
                              borderColor: 'purple.500',
                              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
                            }}
                          />
                          <Field.HelperText fontSize="xs">
                            Describe los términos principales del acuerdo; la otra parte los verá antes de
                            aceptar o rechazar.
                          </Field.HelperText>
                        </Field.Root>
                      </VStack>
                    )}

                    {acuerdoCreateStep === 2 && (
                      <VStack gap={6} align="stretch">
                        <Box
                          borderWidth="2px"
                          borderColor={acuerdoAdjuntosZoneBorder}
                          bg={acuerdoAdjuntosZoneBg}
                          rounded="xl"
                          p={{ base: 4, md: 6 }}
                          boxShadow="md"
                        >
                          <VStack align="stretch" gap={4}>
                            <HStack gap={4} align="flex-start">
                              <Flex
                                w={14}
                                h={14}
                                rounded="xl"
                                bg={acuerdoAdjuntosIconBg}
                                color="white"
                                align="center"
                                justify="center"
                                flexShrink={0}
                                boxShadow="md"
                              >
                                <Icon as={FaPaperclip} boxSize={7} />
                              </Flex>
                              <VStack align="start" gap={1} flex={1}>
                                <Heading size="sm" color={acuerdoAdjuntosHeadingColor}>
                                  Archivos adjuntos
                                </Heading>
                                <Text fontSize="sm" color={acuerdoAdjuntosLeadTextColor}>
                                  La otra parte podrá abrirlos y descargarlos desde el detalle del acuerdo. Son la
                                  forma más clara de compartir condiciones, croquis o referencias.
                                </Text>
                              </VStack>
                            </HStack>

                            <input
                              ref={acuerdoAdjuntosInputRef}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              accept=".pdf,.txt,.md,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,image/*"
                              onChange={handleAcuerdoAdjuntosInputChange}
                            />
                            <Button
                              size="lg"
                              colorPalette="purple"
                              onClick={() => acuerdoAdjuntosInputRef.current?.click()}
                              disabled={acuerdoAdjuntosFiles.length >= MAX_ACUERDO_ADJUNTOS_UI}
                              py={7}
                              fontWeight="700"><Icon as={FaPaperclip} boxSize={5} />Seleccionar archivos
                                                      </Button>
                            <Text fontSize="xs" color={acuerdoAdjuntosHintColor}>
                              Formatos: PDF, TXT, Markdown, Word (.doc/.docx), imágenes JPEG, PNG, WebP o GIF. Máximo{' '}
                              {MAX_ACUERDO_ADJUNTOS_UI} archivos por propuesta.
                            </Text>

                            {acuerdoAdjuntosFiles.length === 0 && (
                              <Box
                                borderWidth="2px"
                                borderStyle="dashed"
                                borderColor={acuerdoAdjuntosListItemBorder}
                                rounded="lg"
                                py={5}
                                px={4}
                                bg={acuerdoAdjuntosEmptyDashedBg}
                              >
                                <Text textAlign="center" fontSize="sm" fontWeight="600" color={acuerdoAdjuntosHintColor}>
                                  Pulsa «Seleccionar archivos» para añadir documentación que acompañe tu propuesta
                                </Text>
                              </Box>
                            )}

                            {acuerdoAdjuntosFiles.length > 0 && (
                              <VStack align="stretch" gap={2} pt={1}>
                                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                                  Se incluirán al enviar ({acuerdoAdjuntosFiles.length})
                                </Text>
                                {acuerdoAdjuntosFiles.map((f, idx) => (
                                  <HStack
                                    key={`${f.name}-${f.size}-${f.lastModified}`}
                                    justify="space-between"
                                    py={3}
                                    px={4}
                                    rounded="lg"
                                    bg={acuerdoAdjuntosListItemBg}
                                    borderWidth="1px"
                                    borderColor={acuerdoAdjuntosListItemBorder}
                                    boxShadow="sm"
                                  >
                                    <HStack gap={3} overflow="hidden" flex={1} minW={0}>
                                      <Icon as={FaFileAlt} flexShrink={0} color="purple.500" boxSize={5} />
                                      <Text fontSize="md" fontWeight="600" lineClamp={2}>
                                        {f.name}
                                      </Text>
                                    </HStack>
                                    <IconButton
                                      aria-label="Quitar archivo"
                                      size="sm"
                                      variant="ghost"
                                      colorPalette="purple"
                                      onClick={() =>
                                        setAcuerdoAdjuntosFiles((p) => p.filter((_, i) => i !== idx))
                                      }><Icon as={FaTimes} /></IconButton>
                                  </HStack>
                                ))}
                              </VStack>
                            )}
                          </VStack>
                        </Box>

                        <Box>
                          <Field.Root>
                            <Field.Label fontSize="sm" color="gray.600" fontWeight="600">
                              Notas de texto (opcional)
                            </Field.Label>
                            <Textarea
                              placeholder="Aclaraciones breves que no estén ya en los archivos o en la descripción…"
                              value={acuerdoNotas}
                              onChange={(e) => setAcuerdoNotas(e.target.value)}
                              rows={3}
                              borderColor="gray.200"
                              _hover={{ borderColor: 'gray.300' }}
                              _focus={{
                                borderColor: 'purple.400',
                                boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)',
                              }}
                            />
                            <Field.HelperText fontSize="xs">
                              Si llevas mucha información en PDF o imágenes, aquí solo hace falta un resumen.
                            </Field.HelperText>
                          </Field.Root>
                        </Box>
                      </VStack>
                    )}

                    {acuerdoCreateStep === 3 && (
                      <VStack gap={4} align="stretch">
                        <Box
                          bg="green.50"
                          p={4}
                          rounded="lg"
                          borderWidth="1px"
                          borderColor="green.200"
                        >
                          <Text fontSize="xs" fontWeight="600" color="green.800" textTransform="uppercase">
                            Contraparte
                          </Text>
                          <Text fontWeight="bold" color="green.900">
                            {selectedConversation.name}
                          </Text>
                          {selectedConversation.evento && (
                            <Text fontSize="sm" color="green.700" mt={1}>
                              {selectedConversation.evento.nombre} ·{' '}
                              {new Date(selectedConversation.evento.fecha).toLocaleDateString('es-ES')}
                            </Text>
                          )}
                        </Box>

                        <Box bg={useColorModeValue('orange.50', 'orange.900')} p={4} rounded="lg" borderWidth="1px" borderColor={useColorModeValue('orange.200', 'orange.700')}>
                          <Alert.Root
                            status="warning"
                            variant='subtle'
                            borderRadius="md"
                            borderStartWidth='3px'
                            borderStartColor='colorPalette.solid'>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">Aviso importante</Alert.Title>
                              <Alert.Description fontSize="sm" display="block">
                                Cadencya no se responsabiliza del contenido ni de los acuerdos pactados entre las partes y no
                                intermedia ningún trato legal. Cadencya únicamente facilita la conexión entre entidad y banda
                                mediante su servicio.
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                          <Field.Root mt={3} required>
                            <Checkbox.Root
                              onCheckedChange={(details) => setAcuerdoAceptaDisclaimer(details.checked === true)}
                              checked={acuerdoAceptaDisclaimer}
                            ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>He leído y acepto el aviso legal anterior
                                                        </Checkbox.Label></Checkbox.Root>
                          </Field.Root>
                        </Box>

                        <Box>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                            Descripción
                          </Text>
                          <Box
                            p={3}
                            bg="gray.50"
                            rounded="md"
                            maxH="160px"
                            overflowY="auto"
                            borderWidth="1px"
                            borderColor="gray.200"
                          >
                            <Text fontSize="sm" whiteSpace="pre-wrap">
                              {acuerdoDescription}
                            </Text>
                          </Box>
                        </Box>

                        {acuerdoNotas.trim() && (
                          <Box>
                            <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                              Notas
                            </Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.700">
                              {acuerdoNotas}
                            </Text>
                          </Box>
                        )}

                        {acuerdoAdjuntosFiles.length > 0 && (
                          <Box
                            borderWidth="2px"
                            borderColor={acuerdoAdjuntosZoneBorder}
                            bg={acuerdoAdjuntosZoneBg}
                            rounded="xl"
                            p={4}
                          >
                            <HStack gap={2} mb={3}>
                              <Icon as={FaPaperclip} color="purple.600" boxSize={5} />
                              <Text fontWeight="800" fontSize="md" color={acuerdoAdjuntosHeadingColor}>
                                Archivos adjuntos ({acuerdoAdjuntosFiles.length})
                              </Text>
                            </HStack>
                            <Text fontSize="xs" color={acuerdoAdjuntosLeadTextColor} mb={3}>
                              Se subirán al enviar la propuesta y estarán disponibles en el detalle del acuerdo.
                            </Text>
                            <VStack align="stretch" gap={2}>
                              {acuerdoAdjuntosFiles.map((f) => (
                                <HStack
                                  key={`${f.name}-${f.size}-${f.lastModified}`}
                                  gap={3}
                                  py={2}
                                  px={3}
                                  rounded="md"
                                  bg={acuerdoAdjuntosListItemBg}
                                  borderWidth="1px"
                                  borderColor={acuerdoAdjuntosListItemBorder}
                                >
                                  <Icon as={FaFileAlt} color="purple.500" boxSize={4} flexShrink={0} />
                                  <Text fontSize="sm" fontWeight="600" lineClamp={2}>
                                    {f.name}
                                  </Text>
                                </HStack>
                              ))}
                            </VStack>
                          </Box>
                        )}

                        <Alert.Root
                          status="info"
                          rounded="lg"
                          variant='subtle'
                          borderStartWidth='3px'
                          borderStartColor='colorPalette.solid'>
                          <Alert.Indicator />
                          <Box>
                            <Alert.Title fontSize="sm" mb={1}>
                              Confirmación requerida
                            </Alert.Title>
                            <Alert.Description fontSize="xs">
                              {selectedConversation.name} deberá aceptar esta propuesta para que el acuerdo sea
                              efectivo.
                              {selectedConversation.evento &&
                                ' El evento permanecerá como «Pendiente» hasta la confirmación.'}
                            </Alert.Description>
                          </Box>
                        </Alert.Root>
                      </VStack>
                    )}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer bg={useColorModeValue('gray.50', 'gray.800')} roundedBottom="md">
                <HStack w="full" justify="space-between" flexWrap="wrap" gap={2}>
                  <Button
                    variant="ghost"
                    onClick={() => setAcuerdoCreateStep((s) => Math.max(0, s - 1))}
                    disabled={acuerdoCreateStep === 0 || isSubmittingAcuerdo}
                    _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                  >
                    Atrás
                  </Button>
                  <HStack gap={2}>
                    <Button variant="ghost" onClick={onAcuerdoModalClose} disabled={isSubmittingAcuerdo}>
                      Cancelar
                    </Button>
                    {acuerdoCreateStep < ACUERDO_CREATE_STEPS.length - 1 ? (
                      <Button
                        colorPalette="purple"
                        onClick={() =>
                          setAcuerdoCreateStep((s) =>
                            Math.min(ACUERDO_CREATE_STEPS.length - 1, s + 1),
                          )
                        }
                        disabled={!canAdvanceAcuerdoCreateStep(acuerdoCreateStep)}
                      >
                        Siguiente
                      </Button>
                    ) : (
                      <Button
                        colorPalette="purple"
                        onClick={handleCreateAcuerdo}
                        loading={isSubmittingAcuerdo}
                        fontWeight="700"
                        disabled={!acuerdoDescription.trim()}><Icon as={FaHandshake} />Enviar propuesta
                                          </Button>
                    )}
                  </HStack>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal para aceptar acuerdo */}
      <Dialog.Root open={isRespondModalOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onRespondModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={2}>
                  <Icon as={FaCheckCircle} color="green.500" />
                  <Text>Confirmar Acuerdo</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  {activeAcuerdo && (
                    <Box
                      w="full"
                      p={4}
                      bg="gray.50"
                      rounded="md"
                      borderLeft="4px solid"
                      borderColor="purple.500"
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="bold">Detalles de la solicitud:</Text>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          variant="outline"
                          onClick={onAcuerdoDetailsOpen}><Icon as={FaInfoCircle} />Ver todos los detalles
                                              </Button>
                      </HStack>
                    </Box>
                  )}
                  
                  <Field.Root>
                    <Field.Label>Notas de respuesta (opcional)</Field.Label>
                    <Textarea
                      placeholder="Añade cualquier comentario..."
                      value={respuestaNotas}
                      onChange={(e) => setRespuestaNotas(e.target.value)}
                      rows={2}
                    />
                  </Field.Root>

                  {userType === 'banda' ? (
                    <Alert.Root
                      status="info"
                      size="sm"
                      rounded="md"
                      variant='subtle'
                      borderStartWidth='3px'
                      borderStartColor='colorPalette.solid'>
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">Al aceptar, ocurre lo siguiente</Alert.Title>
                        <Alert.Description fontSize="xs" as="div">
                          <List.Root as='ul' gap={1} pl={1} mt={2} mb={0}>
                            <List.Item>
                              Confirmas el acuerdo con {getProfileName(activeAcuerdo?.solicitante)} y el evento pasa a confirmado.
                            </List.Item>
                            <List.Item>
                              La fecha queda <strong>bloqueada en tu calendario de disponibilidad</strong>.
                            </List.Item>
                            <List.Item>
                              Se crea una <strong>nueva actuación</strong> en Gestión (Actuaciones) para completar ficha, plantilla y encuesta.
                            </List.Item>
                          </List.Root>
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                  ) : (
                    <Alert.Root status="success" size="sm" rounded="md">
                      <Alert.Indicator />
                      <Text fontSize="sm">
                        Al aceptar, confirmas los términos del acuerdo con {getProfileName(activeAcuerdo?.solicitante)}.
                      </Text>
                    </Alert.Root>
                  )}
                  <Box bg={useColorModeValue('orange.50', 'orange.900')} p={4} rounded="lg" borderWidth="1px" borderColor={useColorModeValue('orange.200', 'orange.700')} w="full">
                    <Alert.Root
                      status="warning"
                      variant='subtle'
                      borderRadius="md"
                      borderStartWidth='3px'
                      borderStartColor='colorPalette.solid'>
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">Aviso importante</Alert.Title>
                        <Alert.Description fontSize="sm" display="block">
                          Cadencya no se responsabiliza del contenido ni de los acuerdos pactados entre las partes y no
                          intermedia ningún trato legal. Cadencya únicamente facilita la conexión entre entidad y banda
                          mediante su servicio.
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                    <Field.Root mt={3} required>
                      <Checkbox.Root
                        onCheckedChange={(details) => setAceptaDisclaimerAceptarAcuerdo(details.checked === true)}
                        checked={aceptaDisclaimerAceptarAcuerdo}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>He leído y acepto el aviso legal anterior
                      </Checkbox.Label></Checkbox.Root>
                    </Field.Root>
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onRespondModalClose}>
                  Volver
                </Button>
                <Button
                  colorPalette="green"
                  onClick={() => handleRespondAcuerdo(true)}
                  loading={isSubmittingAcuerdo}
                  disabled={!aceptaDisclaimerAceptarAcuerdo}><Icon as={FaCheck} />Aceptar Acuerdo
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal para rechazar acuerdo */}
      <Dialog.Root open={isRejectModalOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onRejectModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="red.500" color="white" roundedTop="md">
                <HStack gap={2}>
                  <Icon as={FaTimes} />
                  <Text>Rechazar Acuerdo</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                <VStack gap={4}>
                  {activeAcuerdo && (
                    <Box
                      w="full"
                      p={4}
                      bg="red.50"
                      rounded="md"
                      borderLeft="4px solid"
                      borderColor="red.500"
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="bold">Detalles de la solicitud:</Text>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          variant="outline"
                          onClick={onAcuerdoDetailsOpen}><Icon as={FaInfoCircle} />Ver todos los detalles
                                              </Button>
                      </HStack>
                    </Box>
                  )}
                  
                  <Field.Root>
                    <Field.Label>Motivo del rechazo (opcional)</Field.Label>
                    <Textarea
                      placeholder="Explica por qué rechazas este acuerdo..."
                      value={respuestaNotas}
                      onChange={(e) => setRespuestaNotas(e.target.value)}
                      rows={3}
                    />
                  </Field.Root>
                  
                  <Alert.Root status="warning" size="sm" rounded="md">
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      Al rechazar, el acuerdo propuesto por {getProfileName(activeAcuerdo?.solicitante)} será cancelado. Esta acción no se puede deshacer.
                    </Text>
                  </Alert.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer bg={useColorModeValue('gray.50', 'gray.800')} roundedBottom="md">
                <Button variant="ghost" mr={3} onClick={onRejectModalClose}>
                  Volver
                </Button>
                <Button
                  colorPalette="red"
                  onClick={() => handleRespondAcuerdo(false)}
                  loading={isSubmittingAcuerdo}><Icon as={FaTimes} />Rechazar Acuerdo
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal para cancelar acuerdo */}
      <Dialog.Root open={isCancelModalOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onCancelModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="orange.500" color="white" roundedTop="md">
                <HStack gap={2}>
                  <Icon as={FaTimes} />
                  <Text>Cancelar Propuesta</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                <VStack gap={4}>
                  {activeAcuerdo && (
                    <Box
                      w="full"
                      p={4}
                      bg="orange.50"
                      rounded="md"
                      borderLeft="4px solid"
                      borderColor="orange.500"
                    >
                      <HStack justify="space-between" mb={2}>
                        <Text fontWeight="bold">Detalles de tu propuesta:</Text>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          variant="outline"
                          onClick={onAcuerdoDetailsOpen}><Icon as={FaInfoCircle} />Ver todos los detalles
                                              </Button>
                      </HStack>
                    </Box>
                  )}
                  
                  <Alert.Root status="warning" size="sm" rounded="md">
                    <Alert.Indicator />
                    <Text fontSize="sm">
                      ¿Estás seguro de que quieres cancelar esta propuesta de acuerdo? {getProfileName(activeAcuerdo?.confirmador)} no recibirá más esta solicitud.
                    </Text>
                  </Alert.Root>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer bg={useColorModeValue('gray.50', 'gray.800')} roundedBottom="md">
                <Button variant="ghost" mr={3} onClick={onCancelModalClose}>
                  No, volver
                </Button>
                <Button
                  colorPalette="orange"
                  onClick={() => {
                    handleCancelAcuerdo();
                    onCancelModalClose();
                  }}
                  loading={isSubmittingAcuerdo}><Icon as={FaTimes} />Sí, cancelar propuesta
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de rescisión de acuerdo */}
      <Dialog.Root 
        open={isRescindModalOpen} 
        size='md' 
        placement='center' 
        onOpenChange={e => {
          if (!e.open) {
            onRescindModalClose();
            setRescindReason('');
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="orange.500" color="white" roundedTop="md">
                <HStack gap={3}>
                  <Icon as={FaExclamationTriangle} w={6} h={6} />
                  <Text>Rescindir Acuerdo</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                {selectedConversation && activeAcuerdo && (
                  <VStack gap={4} align="stretch">
                    <Text fontSize="md" lineHeight="tall">
                      Estás a punto de rescindir el acuerdo confirmado para este evento:
                    </Text>
                    
                    {/* Detalles del evento */}
                    <Box
                      bg="orange.50"
                      p={4}
                      rounded="lg"
                      borderWidth="2px"
                      borderColor="orange.200"
                    >
                      <VStack align="stretch" gap={2}>
                        <Text fontSize="lg" fontWeight="bold" color="orange.800">
                          {selectedConversation.evento?.nombre || 'Evento'}
                        </Text>
                        {selectedConversation.evento?.fecha && (
                          <HStack gap={2}>
                            <Icon as={FaCalendarCheck} color="orange.600" />
                            <Text fontSize="sm" color="orange.700">
                              {new Date(selectedConversation.evento.fecha).toLocaleDateString('es-ES', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Text>
                          </HStack>
                        )}
                        <HStack gap={2}>
                          <Icon as={userType === 'banda' ? FaChurch : FaMusic} color="orange.600" />
                          <Text fontSize="sm" color="orange.700" fontWeight="medium">
                            {selectedConversation.name}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Campo para razón de la rescisión */}
                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="medium">
                        Razón de la rescisión (opcional)
                      </Field.Label>
                      <Textarea
                        placeholder="Explica por qué rescinde el acuerdo..."
                        value={rescindReason}
                        onChange={(e) => setRescindReason(e.target.value)}
                        rows={3}
                        resize="vertical"
                      />
                    </Field.Root>

                    <Alert.Root status="warning" rounded="lg">
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">El acuerdo será rescindido</Alert.Title>
                        <Alert.Description fontSize="xs">
                          Al rescindir, el evento volverá a estado pendiente. La {userType === 'banda' ? 'cofradía' : 'banda'} será notificada de tu decisión.
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="ghost"
                  mr={3}
                  onClick={() => {
                    onRescindModalClose();
                    setRescindReason('');
                  }}
                  disabled={isSubmittingAcuerdo}
                >
                  Cancelar
                </Button>
                <Button
                  colorPalette="orange"
                  onClick={handleRescindAcuerdo}
                  loading={isSubmittingAcuerdo}
                  loadingText="Rescindiendo..."><Icon as={FaExclamationTriangle} />Rescindir acuerdo
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de detalles del acuerdo (mismo diseño que en ficha de evento) */}
      {activeAcuerdo && (
        <AcuerdoDetailsModal
          isOpen={isAcuerdoDetailsOpen}
          onClose={onAcuerdoDetailsClose}
          acuerdo={activeAcuerdo}
          evento={
            selectedConversation?.evento
              ? {
                  eventName: selectedConversation.evento.nombre,
                  date: String(selectedConversation.evento.fecha || '').split('T')[0],
                  timeStart: selectedConversation.evento.horaInicio || selectedConversation.evento.hora || '',
                  location: selectedConversation.evento.ubicacion || '',
                }
              : null
          }
          otherParty={{
            name: selectedConversation?.name || 'Contraparte',
            avatar: selectedConversation?.avatar || '',
          }}
          userType={userType}
        />
      )}

      {/* Modal de Reseña */}
      <ReviewForm
        isOpen={isReviewModalOpen}
        onClose={onReviewModalClose}
        evento={eventoParaResenar}
        onReviewSubmitted={handleReviewSubmitted}
        userType={userType}
      />
    </AppLayout>
  );
};

export default Messages;
